import MomentUtils from '@date-io/moment'
import { MuiPickersUtilsProvider } from 'akixi-material-ui-pickers'
import moment from 'moment'
import React, {
    ForwardedRef,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react'
import {
    ConfigureReport as ConfigureReportLanguage,
    Date as DataLanguage,
    DaysOfTheWeek as DaysOfTheWeekLanguage,
    FieldsTab as FieldsTabLanguage,
    TimeFormats
} from '../../../i18n'
import { ReportTypes } from '../../NonReact/constants/ReportTypes'
// Utilities imports.
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { handleReportUpdateRenameActiveRptDescription } from '../../NonReact/utils/RptPortal'
import { InternalAPIError } from '../Common/API'
import { HelpTag } from '../Common/Components/HelpTag/HelpTag'
import { IconName } from '../Common/Components/Icon'
import ModalExitPromptContext, { ModalExitPromptType } from '../Common/Components/Modal/ModalExitPromptContext'
import { Switch } from '../Common/Components/Switch/Switch'
// Commons imports.
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import {
    ANY_VALUE_NUMBER_NUMERIC,
    ANY_VALUE_PARTITION_GUID,
    ANY_VALUE_STRING_DEFAULT,
    NOT_DEFINED_VALUE_STRING
} from '../Common/Constants/DefaultValues'
import { LOCALE_EN_GB } from '../Common/Constants/Locale'
import { PAPERSIZE_A4_OPTION_VALUE } from '../Common/Constants/PaperSizes'
import { ListReportDisplayedAsValues, WallboardDisplayedAsValues } from '../Common/Constants/ReportDisplayedAsValues'
import { REPORT_STAT_FLAGS_TYPE_NONE } from '../Common/Constants/ReportStatFlags'
// Contexts imports.
import { LanguageContext, MessageContext, ServletPathContext } from '../Common/Contexts'
import AdvancedModeContext, { AdvancedModeContextType } from '../Common/Contexts/AdvancedModeContext'
import ConfigureReportSettingsContext, {
    ConfigureReportSettingsContextType
} from '../Common/Contexts/ConfigureReportSettingsContext'
import { ModalContext, ModalContextOutputType } from '../Common/Contexts/ModalContext'
import UserInfoContext, { UserInfoContextType } from '../Common/Contexts/UserInfoContext'
import {
    ReactState,
    RptConfigurationDataAddDto,
    RptConfigurationDataRequestDto,
    RptConfigurationDataResponseDto,
    RptConfigurationDataUpdateDto,
    RptConfigurationDataUpdateScheduleDto,
    RptFilterEntityInfo,
    TelephoneServerEntityDataItem
} from '../Common/Types/Types'
import { CURRENCYDEF_DEFAULT, mapCurrencyIdToCurrencySymbol } from '../Common/Utils/Currency'
import { setModalHelpTopicID } from '../Common/Utils/HelpPortal'
import {
    getFolderFromReportDescription,
    getReportNameFromReportDescription,
    prepareReportDescriptionForAddUpdateRequest
} from '../Common/Utils/Reports'
import { mapStatisticsDisplayAndStatisticsStringToStatsConfigs } from '../Common/Utils/ReportStatistics'
import {
    convertDateToDateString,
    convertDateToDurationDateTimeString,
    convertDateToDurationTimeString,
    convertDurationAsFormattedStringToSeconds,
    convertDurationInSecondsToFormattedString,
    convertDurationTimeStringToDate
} from '../Common/Utils/TimeFormats'
import { newReportCreated$ } from '../../NonReact/common/RxJS/NewReportFromPopoutState'
import { personalDashboardUpdate$ } from '../../NonReact/common/RxJS/PersonalDashboardState'
import { fetchReportList } from '../../NonReact/common/RxJS/ReportListState'
import ACDCodesTab from './ACDCodesTab'
import AdvancedTab from './AdvancedTab'
import { REPORT_PRINTABLE_RENDERING_STATVALUEINCLUSION_TYPE_MAIN } from './AdvancedTab/Constants'
import AlarmsTab from './AlarmsTab'
import { iREPORT_ALARM_CAPABILITY_NONE } from './AlarmsTab/Constants'
// Data
import {
    addReport,
    retrieveAvailableStatisticsFields,
    retrieveFilterEntityInfo,
    retrieveReportConfigurationData,
    retrieveReportProperties,
    updateReportConfiguration
} from './API'
import {
    mapACDNACode,
    mapConfiguredDashboardReportsToStatisticsDisplayString,
    mapDashboardLayoutStyleToStatisticsSettingsString,
    mapDaysStatePropertyToDaysOfWeekBitmap,
    mapDaysStateStringValueToDayApiValue,
    mapFinancialDecimalSeparatorToStringValue,
    mapFinancialGroupingSeparatorToStringValue,
    mapResponseDayOfTheWeekToDayOfWeekString,
    mapResponseDaysOfTheWeekToDaysOfWeekArray,
    mapResponseFilterCallChannelsBitmapToChannelTypes,
    mapResponseToAutomaticTextTruncationEnabled,
    mapResponseToIsPrintShowingContentRowsEnabled,
    mapResponseToIsPrintShowingFooterRowEnabled,
    mapResponseToIsPrintShowingHeaderRowEnabled,
    mapResponseToIsPrintShowingSpacerRowsEnabled,
    mapResponseToIsShowSummaryHeaderEnabled,
    mapResponseToShowingContentRowsEnabled,
    mapResponseToShowingFooterRowEnabled,
    mapResponseToShowingHeaderRowEnabled,
    mapResponseToShowingLargeBLFToolTipsEnabled,
    mapResponseToShowingSpacerRowsEnabled,
    mapStateChannelTypesValuesToRequestFilterChannels,
    mapStatePropertiesToPrintViewFlags,
    mapStatePropertiesToReportViewFlags,
    mapStatisticsConfigurationsToRequestStatisticsDisplayString,
    mapStatisticsConfigurationsToRequestStatisticsSettingsString,
    mapStatisticsDisplayToListOfDashboardReports,
    mapStatisticsSettingsToDashboardLayout,
    toReportListItems
} from './ApiMapper'
import {
    getHelpTopicByTab,
    ReportTabID,
    TELEPHONY_SYSTEM_TYPE_UNKNOWN,
    TELEPHONY_SYSTEMS_NOT_SUPPORTING_ACD_CODES,
    ZOOM_LEVEL_DEFAULT_VALUE
} from './Constants'
import DashboardTab from './DashboardTab'
import { DashboardLayoutStyle, ReportListItemType } from './DashboardTab/Types'
import FieldsTab from './FieldsTab'
import { STATISTICS_UNIT_TYPE_CURRENCY } from './FieldsTab/Constants'
import { AvailableStatisticsFieldsArray, StatisticsConfiguration, StatProps } from './FieldsTab/Types'
import FilterTab from './FilterTab'
import { FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY, FILTER_LOCATION_ANY } from './FilterTab/Constants'
import FinancialTab from './FinancialTab'
import {
    FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP,
    FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA
} from './FinancialTab/Constants'
import FooterActions from './FooterActions'
import OtherTab from './OtherTab'
import { REPORT_VALUE_COMPARISON_TYPE_NONE } from './OtherTab/Constants'
import { PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART_IF_AVAILABLE } from './PrintTab/Constants'
import PrintTab from './PrintTab/PrintTab'
// Sub imports.
import { HelpTagTitles, Tags } from '../Common/API/Contentful/Constants'
import { BackendPermissionNames } from '../Common/Constants/Permissions'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { MemoAndReturn, useMemoRefAndReturn } from '../Common/Hooks/useMemoRef'
import { $debug } from '../Common/Utils/ConsoleUtils'
import ReportTab from './ReportTab'
import {
    CallIntervalTypes,
    CHART_PROVIDER_OPTION_VALUE_HIGH_CHARTS,
    CHART_STYLE_OPTION_VALUE_LINE,
    REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE,
    REPORT_CALLS_BY_ONE,
    ZOOM_LEVEL_VALUE_AUTO
} from './ReportTab/Constants'
import { REPORT_DURATION_DAY_CURRENT, REPORT_DURATION_REALTIME } from './ReportTab/DateTimeSection/Constants'
import SchedulingTab from './SchedulingTab'
import { SCHEDULEREPORT_FORMAT_TYPE_PDF, SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE } from './SchedulingTab/Constants'
import SettingsTab from './SettingsTab'
import SLAKPITab from './SLAKPITab'
import TabsContentLoader from './TabsContentLoader'
import {
    ACDNACodeValue,
    CurrencyDisplayFormatSettings,
    MonetaryCallAssignmentsValues,
    ReportDefinition,
    ReportDefs,
    RetrieveStatInfoRequestParameters
} from './Types'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { AkixiWindow, NewReport } from '../../NonReact/common/Types/Types'
import { $window } from '../../NonReact/common/AkixiWindow'
import { retrieveReportList } from '../../NonReact/common/API/Reporting'

/**
 * Component properties type.
 */
type Props = {
    stateRef: ForwardedRef<unknown>
    closeModal: () => void
    reportID?: string
    refreshReportListDataAndStartReport?: (reportID: string, reportDesc: string) => void
    uuid: string
    defaultFolderName?: string
}

/**
 * Configuration tabs container component.
 * @param closeModal Modal close callback.
 * @param reportID Report ID in case of update modal.
 * @param refreshReportListDataAndStartReport Callback method to reload report list data and start report with provided ID - only needed in case of Add action.
 */
// eslint-disable-next-line
const ConfigurationTabs: React.FC<Props> = ({
    stateRef,
    closeModal,
    reportID,
    refreshReportListDataAndStartReport,
    uuid,
    defaultFolderName
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    language.loadBundle(DaysOfTheWeekLanguage.NAME)
    language.loadBundle(DataLanguage.NAME)
    language.loadBundle(TimeFormats.NAME)
    language.loadBundle(FieldsTabLanguage.NAME)
    language.loadBundle(ConfigureReportLanguage.NAME)

    const servletPath: string = useContext(ServletPathContext) as string
    const { displayError }: MessageContextType = useContext(MessageContext)
    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const { popoutMode, setDisablePopoutOption }: ModalContextOutputType = useContext(ModalContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const isPersonalDashboard: MutableRefObject<boolean> = useRef<boolean>(false)

    useEffect(() => {
        personalDashboardUpdate$.observable.subscribe((pdUpdate?: boolean): void => {
            isPersonalDashboard.current = !!pdUpdate
        })
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { configurationTabs }: AkixiWindow = $window
    const { isDateTimeOnly }: ConfigureReportSettingsContextType = useContext(ConfigureReportSettingsContext)

    const percentageFormatString: string = language?.get(FieldsTabLanguage.PercentageFormatString)

    const [reportDefs, setReportDefs]: ReactState<Definable<ReportDefs>> = useState<Definable<ReportDefs>>(undefined)
    const [filterEntityInfo, setFilterEntityInfo]: ReactState<Definable<RptFilterEntityInfo>> =
        useState<Definable<RptFilterEntityInfo>>(undefined)

    // State variables containing original data of existing report in case of modify dialog.
    const [reportModifyOriginalReportStyleReportType, setReportModifyOriginalReportStyleReportType] = useState<
        ReportTypes | undefined
    >()
    const [reportModifyOriginalTelSys, setReportModifyOriginalTelSys] = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [reportModifyOriginalPartitions, setReportModifyOriginalPartitions] = useState<string[]>([
        ANY_VALUE_PARTITION_GUID
    ])

    const [selectedReportStyle, setSelectedReportStyle]: ReactState<Definable<ReportDefinition>> =
        useState<Definable<ReportDefinition>>(undefined)
    const currentReportStyleAlarmTypes: number = selectedReportStyle?.AlarmTypes || 0

    // Flag whether uUser is licensed for current report.
    const isUserLicensedForCurrentReportStyle: boolean =
        !!filterEntityInfo &&
        !!selectedReportStyle &&
        (Number(filterEntityInfo?.EntityInfo.ReportCapabilityInfo.StatFlags) & selectedReportStyle?.StatFlagsType) >
            REPORT_STAT_FLAGS_TYPE_NONE

    // eslint-disable-next-line
    const [isReportDataLoading, setReportDataLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isFilterEntityInfoLoading, setFilterEntityInfoLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isStatsInfoLoading, setStatsInfoLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isStatsInfoUpdateLoading, setStatsInfoUpdateLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isReportListLoading, setReportListLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)

    const reportLoaded: MutableRefObject<boolean> = useRef<boolean>(IsVarEmpty(reportID))

    // Fields Tab Properties
    const [currentlyAvailableStatisticsFields, setCurrentlyAvailableStatisticsFields]: ReactState<
        Definable<AvailableStatisticsFieldsArray>
    > = useState<Definable<AvailableStatisticsFieldsArray>>()
    const [hasStatConfigurationsLoaded, setHasStatConfigurationsLoaded]: ReactState<boolean> = useState<boolean>(false)
    const [configuredStatisticsConfigurations, setConfiguredStatisticsConfigurations]: ReactState<
        StatisticsConfiguration[]
    > = useState<StatisticsConfiguration[]>([])

    // Basic/Advanced switch.
    const { isAdvancedMode, setAdvancedMode }: AdvancedModeContextType = useContext(AdvancedModeContext)

    // Report tab properties.
    const [reportName, setReportName]: ReactState<string> = useState<string>('')
    const [folderPath, setFolderPath]: ReactState<string> = useState<string>(defaultFolderName ?? '')
    const [totalCallByInterval, setTotalCallByInterval]: ReactState<number> = useState<number>(
        REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE
    )
    const [callByIntervalType, setCallByIntervalType]: ReactState<CallIntervalTypes> = useState<number>(
        CallIntervalTypes.MINUTES
    )
    const [note, setNote]: ReactState<string> = useState<string>('')

    const isWallboardReport: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === ReportTypes.DESKTOP_WALLBOARD,
        [selectedReportStyle?.ReportType]
    )
    const isDashboardReport: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === ReportTypes.DASHBOARD,
        [selectedReportStyle?.ReportType]
    )
    const isExternalReport: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === ReportTypes.EXTERNAL_CONTENT,
        [selectedReportStyle?.ReportType]
    )
    const isTotalCallsByMinutesInterval: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === ReportTypes.TOTAL_CALL_BY_INTERVAL,
        [selectedReportStyle?.ReportType]
    )
    const isCallsByInterval: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === ReportTypes.CALL_BY_INTERVAL,
        [selectedReportStyle?.ReportType]
    )
    const isListReport: boolean = useMemo(
        (): boolean => !isDashboardReport && !isWallboardReport && !isExternalReport,
        [selectedReportStyle?.ReportType]
    )

    const [displayedAsWallboardReport, setDisplayedAsWallBoardReport]: ReactState<number> = useState<number>(
        WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_GRID
    )

    const [zoomLevel, setZoomLevel]: ReactState<string> = useState<string>(String(ZOOM_LEVEL_DEFAULT_VALUE))
    const [isZoomLevelCustom, setIsZoomLevelCustom]: ReactState<boolean> = useState<boolean>(false)

    const [displayedAsListReport, setDisplayedAsListReport]: ReactState<ListReportDisplayedAsValues> =
        useState<ListReportDisplayedAsValues>(
            ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_DETAILEDTABLEVIEW
        )

    const [chartType, setChartType]: ReactState<number> = useState<number>(CHART_STYLE_OPTION_VALUE_LINE)
    const [chartProvider, setChartProvider]: ReactState<number> = useState<number>(
        CHART_PROVIDER_OPTION_VALUE_HIGH_CHARTS
    )

    const [externalURL, setExternalURL]: ReactState<string> = useState<string>('')

    const [isShowingHeaderRowEnabled, setIsShowingHeaderRowEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isShowingFooterRowEnabled, setIsShowingFooterRowEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isShowingContentRowsEnabled, setIsShowingContentRowsEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isShowingSpacerRowsEnabled, setIsShowingSpacerRowsEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)

    const [isShowingLargeBLFToolTipsEnabled, setIsShowingLargeBLFToolTipsEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isAutomaticTextTruncationEnabled, setIsAutomaticTextTruncationEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)

    // Report Tab - Date / Time section properties
    const [dateTimeOptionSelected, setDateTimeOptionSelected]: ReactState<number> =
        useState<number>(REPORT_DURATION_REALTIME)
    const [reportTimeRangeStart, setReportTimeRangeStart]: ReactState<Date> = useState<Date>(
        new Date('1970-01-01T00:00:00.000')
    )
    const [reportTimeRangeEnd, setReportTimeRangeEnd]: ReactState<Date> = useState<Date>(
        new Date('1970-01-01T23:59:59.999')
    )
    const [reportDateTimeRangeStart, setReportDateTimeRangeStart]: ReactState<Date> = useState<Date>(
        moment().subtract(2, 'month').startOf('month').startOf('day').toDate()
    ) // Wind back to the beginning of 2 months ago from now.
    const [reportDateTimeRangeEnd, setReportDateTimeRangeEnd]: ReactState<Date> = useState<Date>(
        moment().endOf('day').toDate()
    )
    const [reportDateTimeDaysOfTheWeek, setReportDateTimeDaysOfTheWeek]: ReactState<string[]> = useState<string[]>([
        `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`
    ])
    const [reportDateTimeLastXDays, setReportDateTimeLastXDays]: ReactState<number | string> = useState<
        number | string
    >(30)

    // Dashboard tab properties
    const [availableReportListItems, setAvailableReportListItems]: ReactState<ReportListItemType[]> = useState<
        ReportListItemType[]
    >([])
    const [configuredDashboardReports, setConfiguredDashboardReports]: ReactState<string[]> = useState<string[]>([])
    const [selectedDashboardLayoutStyle, setSelectedDashboardLayoutStyle]: ReactState<DashboardLayoutStyle> =
        useState<DashboardLayoutStyle>(DashboardLayoutStyle.HorizontalSplit)

    // Alarms tab properties.
    const [isOnAvailableStateEnabled, setIsOnAvailableStateEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [onAvailableState, setOnAvailableState]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(30, language)
    )

    const [isOnBusyStateEnabled, setIsOnBusyStateEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [onBusyState, setOnBusyState]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(300, language)
    )

    const [isOnNAOrDNDStateEnabled, setIsOnNAOrDNDStateEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [onNAOrDNDState, setOnNAOrDNDState]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(180, language)
    )

    const [isOnWrapupStateEnabled, setIsOnWrapupStateEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [onWrapupState, setOnWrapupState]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(180, language)
    )

    const isOnInbAlertingCheckedByDefault: boolean = useMemo(
        (): boolean => !!reportID && !isWallboardReport,
        [reportID, isWallboardReport]
    )
    const [isOnInbAlertingEnabled, setIsOnInbAlertingEnabled]: ReactState<boolean> = useState<boolean>(
        isOnInbAlertingCheckedByDefault
    )
    const [onInbAlerting, setOnInbAlerting]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(15, language)
    )

    const [isOnTalkTimeEnabled, setIsOnTalkTimeEnabled]: ReactState<boolean> = useState<boolean>(false)
    const [onTalkTime, setOnTalkTime]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(300, language)
    )

    const [alarmOnNewAbandoned, setAlarmOnNewAbandoned]: ReactState<boolean> = useState<boolean>(false)

    const [playSound, setPlaySound]: ReactState<boolean> = useState<boolean>(false)
    const [activeWindow, setActiveWindow]: ReactState<boolean> = useState<boolean>(false)

    // Print tab properties
    const [printViewChartInclusion, setPrintViewChartInclusion]: ReactState<number> = useState<number>(
        PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART_IF_AVAILABLE
    )

    const [isPrintShowSummaryHeaderEnabled, setIsPrintShowSummaryHeaderEnabled]: ReactState<boolean> =
        useState<boolean>(true)

    const [isPrintShowingHeaderRowEnabled, setIsPrintShowingHeaderRowEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isPrintShowingFooterRowEnabled, setIsPrintShowingFooterRowEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isPrintShowingContentRowsEnabled, setIsPrintShowingContentRowsEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)
    const [isPrintShowingSpacerRowsEnabled, setIsPrintShowingSpacerRowsEnabled]: ReactState<boolean> =
        useState<boolean>(isListReport)

    const [isReportChartingFeatureEnabled, setIsReportChartingFeatureEnabled]: ReactState<boolean> =
        useState<boolean>(false)

    // Advanced tab properties.
    const [includeCallsForGrpDev, setIncludeCallsForGrpDev] = useState<boolean>(false)
    const [calcACDStatsForAgntsSignedIntoSpecGrp, setCalcACDStatsForAgntsSignedIntoSpecGrp] = useState<boolean>(false)
    const [calcACDStatsForAgntsSignedIntoSpecGrpWasDefaulted, setCalcACDStatsForAgntsSignedIntoSpecGrpWasDefaulted] =
        useState<boolean>(false)

    const [considerCallsMvdToUnresAsOverflwd, setConsiderCallsMvdToUnresAsOverflwd]: ReactState<boolean> =
        useState<boolean>(false)

    const [maxFilterListLimit, setMaxFilterListLimit]: ReactState<Definable<number>> = useState<Definable<number>>(150)
    const [maxRptRowDisplLimit, setMaxRptRowDisplLimit]: ReactState<Definable<number>> =
        useState<Definable<number>>(250)

    const [formatCSVStatValuesType, setFormatCSVStatValuesType]: ReactState<number> = useState<number>(
        REPORT_PRINTABLE_RENDERING_STATVALUEINCLUSION_TYPE_MAIN
    )

    const [enableRptRequestAudit, setEnableRptRequestAudit]: ReactState<boolean> = useState<boolean>(false)

    // Scheduling tab properties.
    const [isScheduleActive, setIsScheduleActive]: ReactState<boolean> = useState<boolean>(false)

    const [scheduleReportOn, setScheduleReportOn]: ReactState<number> = useState<number>(
        SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE
    )

    const [runOnDate, setRunOnDate]: ReactState<OnlyNullable<Date>> = useState<OnlyNullable<Date>>(null)
    const [runAtTime, setRunAtTime]: ReactState<Date> = useState<Date>(new Date('2020-01-01T21:00:00'))
    const [runOnDays, setRunOnDays]: ReactState<string[]> = useState<string[]>([
        `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`,
        `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`
    ])
    const [runAtMinute, setRunAtMinute]: ReactState<Date> = useState<Date>(new Date('2020-01-01T00:00:00'))
    const [runOnDayInWeek, setRunOnDayInWeek]: ReactState<string> = useState<string>(
        `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`
    )
    const [runOnDayInMonth, setRunOnDayInMonth]: ReactState<number> = useState<number>(31)
    const [runBetweenStart, setRunBetweenStart]: ReactState<Date> = useState<Date>(new Date('2020-01-01T10:00:00'))
    const [runBetweenEnd, setRunBetweenEnd]: ReactState<Date> = useState<Date>(new Date('2020-01-01T16:00:00'))

    const [deleteSchedule, setDeleteSchedule]: ReactState<boolean> = useState<boolean>(false)
    const [resetSchedule, setResetSchedule]: ReactState<boolean> = useState<boolean>(false)

    const [targetRptFormat, setTargetRptFormat]: ReactState<number> = useState<number>(SCHEDULEREPORT_FORMAT_TYPE_PDF)
    const [useLanguage, setUseLanguage]: ReactState<string> = useState<string>(LOCALE_EN_GB)
    const [paperSize, setPaperSize]: ReactState<string> = useState<string>(PAPERSIZE_A4_OPTION_VALUE)

    const [autoCalcDuration, setAutoCalcDuration]: ReactState<boolean> = useState<boolean>(true)
    const [bundleEmailAttachAsZip, setBundleEmailAttachAsZip]: ReactState<boolean> = useState<boolean>(false)
    const [runWithSimilarSchedules, setRunWithSimilarSchedules]: ReactState<boolean> = useState<boolean>(true)

    const [scheduleNextRunningAt, setScheduleNextRunningAt]: ReactState<string> = useState<string>('')
    const [scheduleLastRunningAt, setScheduleLastRunningAt]: ReactState<string> = useState<string>('')
    const [scheduleStatusError, setScheduleStatusError]: ReactState<string> = useState<string>('')

    // Settings tab properties.
    const [includeInTransitioningSequence, setIncludeInTransitioningSequence]: ReactState<boolean> =
        useState<boolean>(true)
    const [rptDisplIntervalTime, setRptDisplIntervalTime]: ReactState<string> = useState<string>('')

    // SLA/KPI tab properties.
    const [callsAnsWithin, setCallsAnsWithin]: ReactState<Definable<number>> = useState<Definable<number>>(10)
    const [callsAbndWithin, setCallsAbndWithin]: ReactState<Definable<number>> = useState<Definable<number>>(10)
    const [isSrvLvlIgnQuickAbndCallsWithinEnabled, setIsSrvLvlIgnQuickAbndCallsWithinEnabled]: ReactState<boolean> =
        useState<boolean>(false)
    const [srvLvlIgnQuickAbndCallsWithin, setSrvLvlIgnQuickAbndCallsWithin]: ReactState<Definable<number>> =
        useState<Definable<number>>(5)
    const [isSrvLvlIgnOvrflwdCallsWithinEnabled, setIsSrvLvlIgnOvrflwdCallsWithinEnabled]: ReactState<boolean> =
        useState<boolean>(false)
    const [srvLvlIgnOvrflwdCallsWithin, setSrvLvlIgnOvrflwdCallsWithin]: ReactState<Definable<number>> =
        useState<Definable<number>>(5)

    const [isInbStIgnQuickAbndCallsWithinEnabled, setIsInbStIgnQuickAbndCallsWithinEnabled]: ReactState<boolean> =
        useState<boolean>(false)
    const [inbStIgnQuickAbndCallsWithin, setInbStIgnQuickAbndCallsWithin]: ReactState<Definable<number>> =
        useState<Definable<number>>(5)
    const [isInbStIgnOvrflwdCallsWithinEnabled, setIsInbStIgnOvrflwdCallsWithinEnabled]: ReactState<boolean> =
        useState<boolean>(false)
    const [inbStIgnOvrflwdCallsWithin, setInbStIgnOvrflwdCallsWithin]: ReactState<Definable<number>> =
        useState<Definable<number>>(5)

    const [callsInAnsThres1, setCallsInAnsThres1]: ReactState<Definable<number>> = useState<Definable<number>>(15)
    const [callsInAnsThres2, setCallsInAnsThres2]: ReactState<Definable<number>> = useState<Definable<number>>(30)
    const [callsInAnsThres3, setCallsInAnsThres3]: ReactState<Definable<number>> = useState<Definable<number>>(60)
    const [callsInAnsThres4, setCallsInAnsThres4]: ReactState<Definable<number>> = useState<Definable<number>>(90)
    const [callsInAnsThres5, setCallsInAnsThres5]: ReactState<Definable<number>> = useState<Definable<number>>(120)
    const [callsInAnsThres6, setCallsInAnsThres6]: ReactState<Definable<number>> = useState<Definable<number>>(300)

    const [callsInAbndThres1, setCallsInAbndThres1]: ReactState<Definable<number>> = useState<Definable<number>>(60)
    const [callsInAbndThres2, setCallsInAbndThres2]: ReactState<Definable<number>> = useState<Definable<number>>(90)
    const [callsInAbndThres3, setCallsInAbndThres3]: ReactState<Definable<number>> = useState<Definable<number>>(120)
    const [callsInAbndThres4, setCallsInAbndThres4]: ReactState<Definable<number>> = useState<Definable<number>>(150)
    const [callsInAbndThres5, setCallsInAbndThres5]: ReactState<Definable<number>> = useState<Definable<number>>(180)
    const [callsInAbndThres6, setCallsInAbndThres6]: ReactState<Definable<number>> = useState<Definable<number>>(300)

    const [includeEndedOnHold, setIncludeEndedOnHold]: ReactState<boolean> = useState<boolean>(false)
    const [includeEscaped, setIncludeEscaped]: ReactState<boolean> = useState<boolean>(false)
    const [excludeHeldTime, setExcludeHeldTime]: ReactState<boolean> = useState<boolean>(false)

    // Filter tab properties.
    const [telSys, setTelSys]: ReactState<number> = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [partitions, setPartitions]: ReactState<string[]> = useState<string[]>([ANY_VALUE_PARTITION_GUID])

    const [deviceTypes, setDeviceTypes]: ReactState<number[]> = useState<number[]>([ALL_VALUE])
    const [telephoneNos, setTelephoneNos]: ReactState<string> = useState<string>('')
    const [telephoneNoDescriptions, setTelephoneNoDescriptions]: ReactState<string> = useState<string>('')
    const [devices, setDevices]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [ACDAgents, setACDAgents]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [filterLocationToFrom, setFilterLocationToFrom]: ReactState<number> = useState<number>(FILTER_LOCATION_ANY)

    const [channelTypes, setChannelTypes]: ReactState<number[]> = useState<number[]>([
        FILTER_CHANNEL_TYPE_FILTER_OPTION_ANY
    ])
    const [callTypeDirection, setCallTypeDirection]: ReactState<number> = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [answerType, setAnswerType]: ReactState<number> = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [callerID, setCallerID]: ReactState<number> = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [DDIDigits, setDDIDigits]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [accountCodes, setAccountCodes]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [callIdentifier, setCallIdentifier]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [subjectFilter, setSubjectFilter]: ReactState<string> = useState<string>('')
    const [treatConfCallAsExternal, setTreatConfCallAsExternal]: ReactState<boolean> = useState<boolean>(false)

    // ACD Codes tab properties.
    const [statisticSettingsACDNACodes, setStatisticSettingsACDNACodes]: ReactState<ACDNACodeValue[]> = useState<
        ACDNACodeValue[]
    >([])

    const [useCodeDescriptionForACDNACodeStatisticSettings, setUseCodeDescriptionForACDNACodeStatisticSettings] =
        useState<boolean>(true)

    // Financial tab properties.
    const [currencyDisplayFormatSettings, setCurrencyDisplayFormatSettings]: ReactState<CurrencyDisplayFormatSettings> =
        useState<CurrencyDisplayFormatSettings>({
            currencyId: CURRENCYDEF_DEFAULT.currencyID,
            pattern: CURRENCYDEF_DEFAULT.patternDefault,
            groupingSeparator: FINANCIAL_GROUPINGSEPARATOR_OPTION_COMMA,
            decimalSeparator: FINANCIAL_DECIMAL_SEPARATOR_OPTION_FULL_STOP
        })

    const [currencyFormatter, setCurrencyFormatter]: ReactState<CDecimalFormatter> = useState<CDecimalFormatter>(
        (): CDecimalFormatter =>
            // eslint-disable-next-line
            new com.Akixi.Text.CDecimalFormatter(
                currencyDisplayFormatSettings.pattern,
                String.fromCharCode(currencyDisplayFormatSettings.groupingSeparator),
                String.fromCharCode(currencyDisplayFormatSettings.decimalSeparator),
                mapCurrencyIdToCurrencySymbol(currencyDisplayFormatSettings.currencyId)
            )
    )

    const [monetaryCallAssignmentsValues, setMonetaryCallAssignmentsValues]: ReactState<MonetaryCallAssignmentsValues> =
        useState<MonetaryCallAssignmentsValues>({
            inboundCallsAvgOrderVal: currencyFormatter.Format(250),
            inboundStrikeRate: 5,
            outboundCallsAvgOrderVal: currencyFormatter.Format(250),
            outboundStrikeRate: 5
        })

    // Other tab properties
    const [telnosMovedTo, setTelnosMovedTo]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [devicesMovedFrom, setDevicesMovedFrom]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [ACDAgentsMovedFrom, setACDAgentsMovedFrom]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)
    const [callSegmentNumber, setCallSegmentNumber]: ReactState<number> = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [callSegmentStartReason, setCallSegmentStartReason]: ReactState<number> =
        useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [callSegmentEndReason, setCallSegmentEndReason]: ReactState<number> =
        useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [callSegmentChainID, setCallSegmentChainID]: ReactState<string> = useState<string>(ANY_VALUE_STRING_DEFAULT)

    const [ringTimeDistComparisonType, setRingTimeDistComparisonType]: ReactState<number> = useState<number>(
        REPORT_VALUE_COMPARISON_TYPE_NONE
    )
    const [ringTimeDistValue1, setRingTimeDistValue1]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(10, language)
    )
    const [ringTimeDistValue2, setRingTimeDistValue2]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(30, language)
    )
    const [ringTimeSegmentComparisonType, setRingTimeSegmentComparisonType]: ReactState<number> = useState<number>(
        REPORT_VALUE_COMPARISON_TYPE_NONE
    )
    const [ringTimeSegmentValue1, setRingTimeSegmentValue1]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(10, language)
    )
    const [ringTimeSegmentValue2, setRingTimeSegmentValue2]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(30, language)
    )
    const [callTalkTimeComparisonType, setCallTalkTimeComparisonType]: ReactState<number> = useState<number>(
        REPORT_VALUE_COMPARISON_TYPE_NONE
    )
    const [callTalkTimeValue1, setCallTalkTimeValue1]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(120, language)
    )
    const [callTalkTimeValue2, setCallTalkTimeValue2]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(240, language)
    )
    const [totalCallTimeComparisonType, setTotalCallTimeComparisonType]: ReactState<number> = useState<number>(
        REPORT_VALUE_COMPARISON_TYPE_NONE
    )
    const [totalCallTimeValue1, setTotalCallTimeValue1]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(300, language)
    )
    const [totalCallTimeValue2, setTotalCallTimeValue2]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(600, language)
    )
    const [callHeldTimeComparisonType, setCallHeldTimeComparisonType]: ReactState<number> = useState<number>(
        REPORT_VALUE_COMPARISON_TYPE_NONE
    )
    const [callHeldTimeValue1, setCallHeldTimeValue1]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(30, language)
    )
    const [callHeldTimeValue2, setCallHeldTimeValue2]: ReactState<string> = useState<string>(
        convertDurationInSecondsToFormattedString(60, language)
    )

    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(ReportTabID.REPORT)

    const currentlySelectedTelephoneServerType = useMemo(
        () =>
            filterEntityInfo?.EntityInfo.TelSvrs.Data.find(
                (telServer: { ID: string }) => parseInt(telServer.ID, 10) === telSys
            )?.Type,
        [filterEntityInfo, telSys]
    )

    const shouldDisplayACDCodesTab: boolean = useMemo(
        (): boolean =>
            // Or we have already selected server that does support ACD codes
            (!!currentlySelectedTelephoneServerType &&
                currentlySelectedTelephoneServerType !== TELEPHONY_SYSTEM_TYPE_UNKNOWN &&
                TELEPHONY_SYSTEMS_NOT_SUPPORTING_ACD_CODES.find(
                    (type: number): boolean => type === currentlySelectedTelephoneServerType
                ) === undefined) ||
            // Either there is no server that does not support ACD codes overall when All Telephony Server option is selected
            (telSys === ANY_VALUE_NUMBER_NUMERIC &&
                !filterEntityInfo?.EntityInfo.TelSvrs.Data.some(
                    (telServer: TelephoneServerEntityDataItem): boolean =>
                        !!telServer.Type && TELEPHONY_SYSTEMS_NOT_SUPPORTING_ACD_CODES.indexOf(telServer.Type) > -1
                )),
        [filterEntityInfo, telSys]
    )

    const isReportTemplateUser: boolean = useMemo(
        (): boolean => !!userInfo?.Permissions?.NonAdmin?.includes(BackendPermissionNames.REPORT_TEMPLATE_USER),
        [JSON.stringify(userInfo?.Permissions?.NonAdmin)]
    )

    const [isCurrentTelSysHistoricRef, isCurrentTelSysHistoric]: MemoAndReturn<boolean> = useMemoRefAndReturn(
        (): boolean =>
            filterEntityInfo?.EntityInfo?.TelSvrs?.Data.find(
                (telephoneSys: TelephoneServerEntityDataItem): boolean => telephoneSys.ID === `${telSys}`
            )?.HistoricOnly ?? false,
        [JSON.stringify(filterEntityInfo?.EntityInfo?.TelSvrs?.Data), telSys]
    )

    useImperativeHandle(
        stateRef,
        () => ({
            selectedReportStyle,
            currentlyAvailableStatisticsFields,
            configuredStatisticsConfigurations,
            reportName,
            folderPath,
            note,
            displayedAsWallboardReport,
            zoomLevel,
            isZoomLevelCustom,
            displayedAsListReport,
            chartType,
            chartProvider,
            externalURL,
            isShowingHeaderRowEnabled,
            isShowingFooterRowEnabled,
            isShowingContentRowsEnabled,
            dateTimeOptionSelected,
            reportTimeRangeStart,
            reportTimeRangeEnd,
            reportDateTimeRangeStart,
            reportDateTimeRangeEnd,
            reportDateTimeDaysOfTheWeek,
            availableReportListItems,
            configuredDashboardReports,
            selectedDashboardLayoutStyle,
            isOnAvailableStateEnabled,
            onAvailableState,
            isOnBusyStateEnabled,
            onBusyState,
            isOnNAOrDNDStateEnabled,
            onNAOrDNDState,
            isOnWrapupStateEnabled,
            onWrapupState,
            onInbAlerting,
            isOnTalkTimeEnabled,
            onTalkTime,
            alarmOnNewAbandoned,
            playSound,
            activeWindow,
            printViewChartInclusion,
            isPrintShowSummaryHeaderEnabled,
            isPrintShowingHeaderRowEnabled,
            isPrintShowingFooterRowEnabled,
            isPrintShowingContentRowsEnabled,
            isPrintShowingSpacerRowsEnabled,
            isReportChartingFeatureEnabled,
            includeCallsForGrpDev,
            calcACDStatsForAgntsSignedIntoSpecGrp,
            considerCallsMvdToUnresAsOverflwd,
            maxFilterListLimit,
            maxRptRowDisplLimit,
            formatCSVStatValuesType,
            enableRptRequestAudit,
            isScheduleActive,
            scheduleReportOn,
            runOnDate,
            runAtTime,
            runOnDays,
            runAtMinute,
            runOnDayInWeek,
            runOnDayInMonth,
            runBetweenStart,
            runBetweenEnd,
            deleteSchedule,
            resetSchedule,
            targetRptFormat,
            useLanguage,
            paperSize,
            autoCalcDuration,
            bundleEmailAttachAsZip,
            runWithSimilarSchedules,
            scheduleNextRunningAt,
            scheduleLastRunningAt,
            scheduleStatusError,
            includeInTransitioningSequence,
            rptDisplIntervalTime,
            callsAnsWithin,
            callsAbndWithin,
            isSrvLvlIgnQuickAbndCallsWithinEnabled,
            srvLvlIgnQuickAbndCallsWithin,
            isSrvLvlIgnOvrflwdCallsWithinEnabled,
            srvLvlIgnOvrflwdCallsWithin,
            isInbStIgnQuickAbndCallsWithinEnabled,
            isInbStIgnOvrflwdCallsWithinEnabled,
            inbStIgnOvrflwdCallsWithin,
            callsInAnsThres1,
            callsInAnsThres2,
            callsInAnsThres3,
            callsInAnsThres4,
            callsInAnsThres5,
            callsInAnsThres6,
            callsInAbndThres1,
            callsInAbndThres2,
            callsInAbndThres3,
            callsInAbndThres4,
            callsInAbndThres5,
            callsInAbndThres6,
            includeEndedOnHold,
            includeEscaped,
            excludeHeldTime,
            telSys,
            partitions,
            telephoneNos,
            telephoneNoDescriptions,
            devices,
            ACDAgents,
            filterLocationToFrom,
            channelTypes,
            callTypeDirection,
            answerType,
            callerID,
            DDIDigits,
            accountCodes,
            callIdentifier,
            subjectFilter,
            treatConfCallAsExternal,
            statisticSettingsACDNACodes,
            useCodeDescriptionForACDNACodeStatisticSettings,
            currencyDisplayFormatSettings,
            currencyFormatter,
            monetaryCallAssignmentsValues,
            telnosMovedTo,
            devicesMovedFrom,
            ACDAgentsMovedFrom,
            callSegmentNumber,
            callSegmentStartReason,
            callSegmentEndReason,
            callSegmentChainID,
            ringTimeDistComparisonType,
            ringTimeDistValue1,
            ringTimeDistValue2,
            ringTimeSegmentComparisonType,
            ringTimeSegmentValue1,
            ringTimeSegmentValue2,
            callTalkTimeComparisonType,
            callTalkTimeValue1,
            callTalkTimeValue2,
            totalCallTimeComparisonType,
            totalCallTimeValue1,
            totalCallTimeValue2,
            callHeldTimeComparisonType,
            callHeldTimeValue1,
            callHeldTimeValue2
        }),
        [
            selectedReportStyle,
            currentlyAvailableStatisticsFields,
            configuredStatisticsConfigurations,
            reportName,
            folderPath,
            note,
            displayedAsWallboardReport,
            zoomLevel,
            isZoomLevelCustom,
            displayedAsListReport,
            chartType,
            chartProvider,
            externalURL,
            isShowingHeaderRowEnabled,
            isShowingFooterRowEnabled,
            isShowingContentRowsEnabled,
            dateTimeOptionSelected,
            reportTimeRangeStart,
            reportTimeRangeEnd,
            reportDateTimeRangeStart,
            reportDateTimeRangeEnd,
            reportDateTimeDaysOfTheWeek,
            availableReportListItems,
            configuredDashboardReports,
            selectedDashboardLayoutStyle,
            isOnAvailableStateEnabled,
            onAvailableState,
            isOnBusyStateEnabled,
            onBusyState,
            isOnNAOrDNDStateEnabled,
            onNAOrDNDState,
            isOnWrapupStateEnabled,
            onWrapupState,
            onInbAlerting,
            isOnTalkTimeEnabled,
            onTalkTime,
            alarmOnNewAbandoned,
            playSound,
            activeWindow,
            printViewChartInclusion,
            isPrintShowSummaryHeaderEnabled,
            isPrintShowingHeaderRowEnabled,
            isPrintShowingFooterRowEnabled,
            isPrintShowingContentRowsEnabled,
            isPrintShowingSpacerRowsEnabled,
            isReportChartingFeatureEnabled,
            includeCallsForGrpDev,
            calcACDStatsForAgntsSignedIntoSpecGrp,
            considerCallsMvdToUnresAsOverflwd,
            maxFilterListLimit,
            maxRptRowDisplLimit,
            formatCSVStatValuesType,
            enableRptRequestAudit,
            isScheduleActive,
            scheduleReportOn,
            runOnDate,
            runAtTime,
            runOnDays,
            runAtMinute,
            runOnDayInWeek,
            runOnDayInMonth,
            runBetweenStart,
            runBetweenEnd,
            deleteSchedule,
            resetSchedule,
            targetRptFormat,
            useLanguage,
            paperSize,
            autoCalcDuration,
            bundleEmailAttachAsZip,
            runWithSimilarSchedules,
            scheduleNextRunningAt,
            scheduleLastRunningAt,
            scheduleStatusError,
            includeInTransitioningSequence,
            rptDisplIntervalTime,
            callsAnsWithin,
            callsAbndWithin,
            isSrvLvlIgnQuickAbndCallsWithinEnabled,
            srvLvlIgnQuickAbndCallsWithin,
            isSrvLvlIgnOvrflwdCallsWithinEnabled,
            srvLvlIgnOvrflwdCallsWithin,
            isInbStIgnQuickAbndCallsWithinEnabled,
            isInbStIgnOvrflwdCallsWithinEnabled,
            inbStIgnOvrflwdCallsWithin,
            callsInAnsThres1,
            callsInAnsThres2,
            callsInAnsThres3,
            callsInAnsThres4,
            callsInAnsThres5,
            callsInAnsThres6,
            callsInAbndThres1,
            callsInAbndThres2,
            callsInAbndThres3,
            callsInAbndThres4,
            callsInAbndThres5,
            callsInAbndThres6,
            includeEndedOnHold,
            includeEscaped,
            excludeHeldTime,
            telSys,
            partitions,
            telephoneNos,
            telephoneNoDescriptions,
            devices,
            ACDAgents,
            filterLocationToFrom,
            channelTypes,
            callTypeDirection,
            answerType,
            callerID,
            DDIDigits,
            accountCodes,
            callIdentifier,
            subjectFilter,
            treatConfCallAsExternal,
            statisticSettingsACDNACodes,
            useCodeDescriptionForACDNACodeStatisticSettings,
            currencyDisplayFormatSettings,
            currencyFormatter,
            monetaryCallAssignmentsValues,
            telnosMovedTo,
            devicesMovedFrom,
            ACDAgentsMovedFrom,
            callSegmentNumber,
            callSegmentStartReason,
            callSegmentEndReason,
            callSegmentChainID,
            ringTimeDistComparisonType,
            ringTimeDistValue1,
            ringTimeDistValue2,
            ringTimeSegmentComparisonType,
            ringTimeSegmentValue1,
            ringTimeSegmentValue2,
            callTalkTimeComparisonType,
            callTalkTimeValue1,
            callTalkTimeValue2,
            totalCallTimeComparisonType,
            totalCallTimeValue1,
            totalCallTimeValue2,
            callHeldTimeComparisonType,
            callHeldTimeValue1,
            callHeldTimeValue2
        ]
    )

    useEffect(() => {
        if (filterEntityInfo) {
            /**
             * Handle changes to available filter options when they change - update values to sensible defaults if the currently selected values are no longer available.
             */
            if (
                filterEntityInfo.EntityInfo.TelSvrs.Data.find(
                    (telephoneServerData: { ID: string }) => telephoneServerData.ID === String(telSys)
                ) === undefined
            ) {
                // The currently selected telephone server is no longer in the list of available servers, remove it and select
                // the first option received from the server.
                setTelSys(parseInt(filterEntityInfo.EntityInfo.TelSvrs.Data[0].ID, 10))
            }

            // Update partitions based on latest available data.
            const filteredPartitionsData = partitions.filter(
                (partitionID: string) =>
                    filterEntityInfo.EntityInfo.Partitions.Data.find(
                        (partitionData: { ID: string }) => partitionData.ID === partitionID
                    ) !== undefined
            )
            if (filteredPartitionsData.length === 0 && filterEntityInfo.EntityInfo.Partitions.Data.length > 0) {
                // Take the first option as default.
                setPartitions([filterEntityInfo.EntityInfo.Partitions.Data[0].ID])
            } else if (filteredPartitionsData.length !== partitions.length) {
                // Set the partitions value with updated list of data.
                setPartitions(filteredPartitionsData)
            }
        }
    }, [filterEntityInfo])

    useEffect(() => {
        if (filterEntityInfo && selectedReportStyle) {
            /**
             * Updates settings in Advanced tab according to the defaults for current filterEntityInfo / reportStyle.
             */
            if (!reportID || calcACDStatsForAgntsSignedIntoSpecGrpWasDefaulted) {
                // Current modal is an Add Report dialog or there have been user modifications already that applied the defaults.
                const isCalcACDStatsForAgntsSignedIntoSpecGrp =
                    Boolean(
                        filterEntityInfo.EntityInfo.ReportCapabilityInfo
                            .FilterDevicesIfGrpIncACDItemsForGrpDevOnlyDefault
                    ) && selectedReportStyle.FilterDevicesIfGrpIncACDItemsForGrpDevOnly
                setCalcACDStatsForAgntsSignedIntoSpecGrp(isCalcACDStatsForAgntsSignedIntoSpecGrp)
            } else if (
                reportID &&
                !isReportDataLoading &&
                !calcACDStatsForAgntsSignedIntoSpecGrpWasDefaulted &&
                (telSys !== reportModifyOriginalTelSys ||
                    !(
                        partitions.every((value: string) =>
                            reportModifyOriginalPartitions.some(
                                (initialPartition: string) => initialPartition === value
                            )
                        ) && partitions.length === reportModifyOriginalPartitions.length
                    ) ||
                    reportModifyOriginalReportStyleReportType !== selectedReportStyle.ReportType)
            ) {
                // Current modal is an update dialog and there was an change to different telSys / partition / report style.
                setCalcACDStatsForAgntsSignedIntoSpecGrpWasDefaulted(true)
                const isCalcACDStatsForAgntsSignedIntoSpecGrp =
                    Boolean(
                        filterEntityInfo.EntityInfo.ReportCapabilityInfo
                            .FilterDevicesIfGrpIncACDItemsForGrpDevOnlyDefault
                    ) && selectedReportStyle.FilterDevicesIfGrpIncACDItemsForGrpDevOnly
                setCalcACDStatsForAgntsSignedIntoSpecGrp(isCalcACDStatsForAgntsSignedIntoSpecGrp)
            }
        }
    }, [filterEntityInfo, selectedReportStyle])

    const getEncodedDeviceFilterString = (deviceFilterStr: string): string =>
        deviceFilterStr
            .split(',')
            .map((deviceID: string): string => {
                if (!isPositiveRange(deviceID)) {
                    // return string with the replaced hyphen
                    return deviceID.replace(/-/g, '\u2013')
                }
                return deviceID // No change needed, return original string
            })
            .join(',')

    const isPositiveRange = (value: string): boolean => {
        // Split the value by hyphen or comma (flexible format)
        const stringParts: string[] = value.split(/[-,]/g)
        // Check for invalid range format (must have exactly 2 parts)
        if (stringParts.length !== 2) {
            return false
        }

        try {
            // Attempt conversion to numbers
            const startNum: number = parseInt(stringParts[0], 10)
            const endEum: number = parseInt(stringParts[1], 10)
            // Handle numerical ranges
            return startNum >= 0 && endEum >= 0
        } catch (error) {
            // Handle potential conversion errors (e.g., invalid format)
            $debug(error as Error)
            return false
        }
    }

    const mapFromPopoutData = (): void => {
        if (configurationTabs) {
            /* eslint-disable */
            const deviceFilterString: string = getEncodedDeviceFilterString(configurationTabs.devices)
            setSelectedReportStyle(configurationTabs.selectedReportStyle)
            setCurrentlyAvailableStatisticsFields(configurationTabs.currentlyAvailableStatisticsFields)
            setConfiguredStatisticsConfigurations(configurationTabs.configuredStatisticsConfigurations)
            setHasStatConfigurationsLoaded(true)
            setReportName(configurationTabs.reportName)
            setFolderPath(configurationTabs.folderPath)
            setNote(configurationTabs.note)
            setDisplayedAsWallBoardReport(configurationTabs.displayedAsWallboardReport)
            setZoomLevel(configurationTabs.zoomLevel)
            setIsZoomLevelCustom(configurationTabs.isZoomLevelCustom)
            setDisplayedAsListReport(configurationTabs.displayedAsListReport)
            setChartType(configurationTabs.chartType)
            setChartProvider(configurationTabs.chartProvider)
            setExternalURL(configurationTabs.externalURL)
            setIsShowingHeaderRowEnabled(configurationTabs.isShowingHeaderRowEnabled)
            setIsShowingFooterRowEnabled(configurationTabs.isShowingFooterRowEnabled)
            setIsShowingContentRowsEnabled(configurationTabs.isShowingContentRowsEnabled)
            setDateTimeOptionSelected(configurationTabs.dateTimeOptionSelected)
            setReportTimeRangeStart(configurationTabs.reportTimeRangeStart)
            setReportTimeRangeEnd(configurationTabs.reportTimeRangeEnd)
            setReportDateTimeRangeStart(configurationTabs.reportDateTimeRangeStart)
            setReportDateTimeRangeEnd(configurationTabs.reportDateTimeRangeEnd)
            setReportDateTimeDaysOfTheWeek(configurationTabs.reportDateTimeDaysOfTheWeek)
            setAvailableReportListItems(configurationTabs.availableReportListItems)
            setConfiguredDashboardReports(configurationTabs.configuredDashboardReports)
            setSelectedDashboardLayoutStyle(configurationTabs.selectedDashboardLayoutStyle)
            setIsOnAvailableStateEnabled(configurationTabs.isOnAvailableStateEnabled)
            setOnAvailableState(configurationTabs.onAvailableState)
            setIsOnBusyStateEnabled(configurationTabs.isOnBusyStateEnabled)
            setOnBusyState(configurationTabs.onBusyState)
            setIsOnNAOrDNDStateEnabled(configurationTabs.isOnNAOrDNDStateEnabled)
            setOnNAOrDNDState(configurationTabs.onNAOrDNDState)
            setIsOnWrapupStateEnabled(configurationTabs.isOnWrapupStateEnabled)
            setOnWrapupState(configurationTabs.onWrapupState)
            setIsOnInbAlertingEnabled(configurationTabs.isOnInbAlertingEnabled)
            setOnInbAlerting(configurationTabs.onInbAlerting)
            setIsOnTalkTimeEnabled(configurationTabs.isOnTalkTimeEnabled)
            setOnTalkTime(configurationTabs.onTalkTime)
            setAlarmOnNewAbandoned(configurationTabs.alarmOnNewAbandoned)
            setPlaySound(configurationTabs.playSound)
            setActiveWindow(configurationTabs.activeWindow)
            setPrintViewChartInclusion(configurationTabs.printViewChartInclusion)
            setIsPrintShowSummaryHeaderEnabled(configurationTabs.isPrintShowSummaryHeaderEnabled)
            setIsPrintShowingHeaderRowEnabled(configurationTabs.isPrintShowingHeaderRowEnabled)
            setIsPrintShowingFooterRowEnabled(configurationTabs.isPrintShowingFooterRowEnabled)
            setIsPrintShowingContentRowsEnabled(configurationTabs.isPrintShowingContentRowsEnabled)
            setIsPrintShowingSpacerRowsEnabled(configurationTabs.isPrintShowingSpacerRowsEnabled)
            setIsReportChartingFeatureEnabled(configurationTabs.isReportChartingFeatureEnabled)
            setIncludeCallsForGrpDev(configurationTabs.includeCallsForGrpDev)
            setCalcACDStatsForAgntsSignedIntoSpecGrp(configurationTabs.calcACDStatsForAgntsSignedIntoSpecGrp)
            setConsiderCallsMvdToUnresAsOverflwd(configurationTabs.considerCallsMvdToUnresAsOverflwd)
            setMaxFilterListLimit(configurationTabs.maxFilterListLimit)
            setMaxRptRowDisplLimit(configurationTabs.maxRptRowDisplLimit)
            setFormatCSVStatValuesType(configurationTabs.formatCSVStatValuesType)
            setEnableRptRequestAudit(configurationTabs.enableRptRequestAudit)
            setIsScheduleActive(configurationTabs.isScheduleActive)
            setScheduleReportOn(configurationTabs.scheduleReportOn)
            setRunOnDate(configurationTabs.runOnDate)
            setRunAtTime(configurationTabs.runAtTime)
            setRunOnDays(configurationTabs.runOnDays)
            setRunAtMinute(configurationTabs.runAtMinute)
            setRunOnDayInWeek(configurationTabs.runOnDayInWeek)
            setRunOnDayInMonth(configurationTabs.runOnDayInMonth)
            setRunBetweenStart(configurationTabs.runBetweenStart)
            setRunBetweenEnd(configurationTabs.runBetweenEnd)
            setDeleteSchedule(configurationTabs.deleteSchedule)
            setResetSchedule(configurationTabs.resetSchedule)
            setTargetRptFormat(configurationTabs.targetRptFormat)
            setUseLanguage(configurationTabs.useLanguage)
            setPaperSize(configurationTabs.paperSize)
            setAutoCalcDuration(configurationTabs.autoCalcDuration)
            setBundleEmailAttachAsZip(configurationTabs.bundleEmailAttachAsZip)
            setRunWithSimilarSchedules(configurationTabs.runWithSimilarSchedules)
            setScheduleNextRunningAt(configurationTabs.scheduleNextRunningAt)
            setScheduleLastRunningAt(configurationTabs.scheduleLastRunningAt)
            setScheduleStatusError(configurationTabs.scheduleStatusError)
            setIncludeInTransitioningSequence(configurationTabs.includeInTransitioningSequence)
            setRptDisplIntervalTime(configurationTabs.rptDisplIntervalTime)
            setCallsAnsWithin(configurationTabs.callsAnsWithin)
            setCallsAbndWithin(configurationTabs.callsAbndWithin)
            setIsSrvLvlIgnQuickAbndCallsWithinEnabled(configurationTabs.isSrvLvlIgnQuickAbndCallsWithinEnabled)
            setSrvLvlIgnQuickAbndCallsWithin(configurationTabs.srvLvlIgnQuickAbndCallsWithin)
            setIsSrvLvlIgnOvrflwdCallsWithinEnabled(configurationTabs.isSrvLvlIgnOvrflwdCallsWithinEnabled)
            setSrvLvlIgnOvrflwdCallsWithin(configurationTabs.srvLvlIgnOvrflwdCallsWithin)
            setIsInbStIgnQuickAbndCallsWithinEnabled(configurationTabs.isInbStIgnQuickAbndCallsWithinEnabled)
            setIsInbStIgnOvrflwdCallsWithinEnabled(configurationTabs.isInbStIgnOvrflwdCallsWithinEnabled)
            setInbStIgnOvrflwdCallsWithin(configurationTabs.inbStIgnOvrflwdCallsWithin)
            setCallsInAnsThres1(configurationTabs.callsInAnsThres1)
            setCallsInAnsThres2(configurationTabs.callsInAnsThres2)
            setCallsInAnsThres3(configurationTabs.callsInAnsThres3)
            setCallsInAnsThres4(configurationTabs.callsInAnsThres4)
            setCallsInAnsThres5(configurationTabs.callsInAnsThres5)
            setCallsInAnsThres6(configurationTabs.callsInAnsThres6)
            setCallsInAbndThres1(configurationTabs.callsInAbndThres1)
            setCallsInAbndThres2(configurationTabs.callsInAbndThres2)
            setCallsInAbndThres3(configurationTabs.callsInAbndThres3)
            setCallsInAbndThres4(configurationTabs.callsInAbndThres4)
            setCallsInAbndThres5(configurationTabs.callsInAbndThres5)
            setCallsInAbndThres6(configurationTabs.callsInAbndThres6)
            setIncludeEndedOnHold(configurationTabs.includeEndedOnHold)
            setIncludeEscaped(configurationTabs.includeEscaped)
            setExcludeHeldTime(configurationTabs.excludeHeldTime)
            setTelSys(configurationTabs.telSys)
            setPartitions(configurationTabs.partitions)
            setTelephoneNos(configurationTabs.telephoneNos)
            setTelephoneNoDescriptions(configurationTabs.telephoneNoDescriptions)
            setDevices(deviceFilterString)
            setACDAgents(configurationTabs.ACDAgents)
            setFilterLocationToFrom(configurationTabs.filterLocationToFrom)
            setChannelTypes(configurationTabs.channelTypes)
            setCallTypeDirection(configurationTabs.callTypeDirection)
            setAnswerType(configurationTabs.answerType)
            setCallerID(configurationTabs.callerID)
            setDDIDigits(configurationTabs.DDIDigits)
            setAccountCodes(configurationTabs.accountCodes)
            setCallIdentifier(configurationTabs.callIdentifier)
            setSubjectFilter(configurationTabs.subjectFilter)
            setTreatConfCallAsExternal(configurationTabs.treatConfCallAsExternal)
            setStatisticSettingsACDNACodes(configurationTabs.statisticSettingsACDNACodes)
            setUseCodeDescriptionForACDNACodeStatisticSettings(
                configurationTabs.useCodeDescriptionForACDNACodeStatisticSettings
            )
            setCurrencyDisplayFormatSettings(configurationTabs.currencyDisplayFormatSettings)
            setCurrencyFormatter(configurationTabs.currencyFormatter)
            setMonetaryCallAssignmentsValues(configurationTabs.monetaryCallAssignmentsValues)
            setTelnosMovedTo(configurationTabs.telnosMovedTo)
            setDevicesMovedFrom(configurationTabs.devicesMovedFrom)
            setACDAgentsMovedFrom(configurationTabs.ACDAgentsMovedFrom)
            setCallSegmentNumber(configurationTabs.callSegmentNumber)
            setCallSegmentStartReason(configurationTabs.callSegmentStartReason)
            setCallSegmentEndReason(configurationTabs.callSegmentEndReason)
            setCallSegmentChainID(configurationTabs.callSegmentChainID)
            setRingTimeDistComparisonType(configurationTabs.ringTimeDistComparisonType)
            setRingTimeDistValue1(configurationTabs.ringTimeDistValue1)
            setRingTimeDistValue2(configurationTabs.ringTimeDistValue2)
            setRingTimeSegmentComparisonType(configurationTabs.ringTimeSegmentComparisonType)
            setRingTimeSegmentValue1(configurationTabs.ringTimeSegmentValue1)
            setRingTimeSegmentValue2(configurationTabs.ringTimeSegmentValue2)
            setCallTalkTimeComparisonType(configurationTabs.callTalkTimeComparisonType)
            setCallTalkTimeValue1(configurationTabs.callTalkTimeValue1)
            setCallTalkTimeValue2(configurationTabs.callTalkTimeValue2)
            setTotalCallTimeComparisonType(configurationTabs.totalCallTimeComparisonType)
            setTotalCallTimeValue1(configurationTabs.totalCallTimeValue1)
            setTotalCallTimeValue2(configurationTabs.totalCallTimeValue2)
            setCallHeldTimeComparisonType(configurationTabs.callHeldTimeComparisonType)
            setCallHeldTimeValue1(configurationTabs.callHeldTimeValue1)
            setCallHeldTimeValue2(configurationTabs.callHeldTimeValue2)
            /* eslint-enable */
        }
    }

    /**
     * Handler method for changes to currencyDisplayFormatSettings that handles all side-effects the change might have.
     * @param newCurrencyDisplayFormatSettings New value of newCurrencyDisplayFormatSettings that should be set.
     */
    const handleCurrencyDisplayFormatSettingsChange = (
        newCurrencyDisplayFormatSettings: CurrencyDisplayFormatSettings
    ): void => {
        /**
         * In case of updates to currency formatter related props, use the old formatter to parse
         * existing financial values and then format them using new configuration.
         */
        // eslint-disable-next-line
        const newFormatter: CDecimalFormatter = new com.Akixi.Text.CDecimalFormatter(
            newCurrencyDisplayFormatSettings.pattern,
            String.fromCharCode(newCurrencyDisplayFormatSettings.groupingSeparator),
            String.fromCharCode(newCurrencyDisplayFormatSettings.decimalSeparator),
            mapCurrencyIdToCurrencySymbol(newCurrencyDisplayFormatSettings.currencyId)
        )

        setMonetaryCallAssignmentsValues((previousValue: MonetaryCallAssignmentsValues) => {
            const previousValueInboundParsed = currencyFormatter.Parse(previousValue.inboundCallsAvgOrderVal)
            const previousValueInboundFormattedWithNew = newFormatter.Format(previousValueInboundParsed)

            const previousValueOutboundParsed = currencyFormatter.Parse(previousValue.outboundCallsAvgOrderVal)
            const previousValueOutboundFormattedWithNew = newFormatter.Format(previousValueOutboundParsed)

            return {
                ...previousValue,
                inboundCallsAvgOrderVal: previousValueInboundFormattedWithNew,
                outboundCallsAvgOrderVal: previousValueOutboundFormattedWithNew
            }
        })

        // Parse & Format statistics values with financial unit type.
        setConfiguredStatisticsConfigurations(
            configuredStatisticsConfigurations.map(
                (statisticsConfiguration: StatisticsConfiguration): StatisticsConfiguration => {
                    if (statisticsConfiguration.unitType === STATISTICS_UNIT_TYPE_CURRENCY) {
                        return {
                            ...statisticsConfiguration,
                            graphDialScaleMaxDefault: newFormatter.Format(
                                currencyFormatter.Parse(statisticsConfiguration.graphDialScaleMaxDefault)
                            ),
                            alarmComparisonValue1: newFormatter.Format(
                                currencyFormatter.Parse(statisticsConfiguration.alarmComparisonValue1)
                            ),
                            alarmComparisonValue2: newFormatter.Format(
                                currencyFormatter.Parse(statisticsConfiguration.alarmComparisonValue1)
                            )
                        }
                    }

                    return statisticsConfiguration
                }
            )
        )

        setCurrencyFormatter(newFormatter)
        setCurrencyDisplayFormatSettings(newCurrencyDisplayFormatSettings)
    }

    /**
     * Prepares object containing all StatInfo request parameters extracted from component current state.
     * @param reportStyle Optional parameter - Report style to be used instead of the one currently in state.
     */
    const prepareStatInfoRequestParamsFromCurrentState = (
        reportStyle: ReportDefinition | undefined = undefined
    ): RetrieveStatInfoRequestParameters => ({
        reportType: reportStyle ? String(reportStyle?.ReportType) : String(selectedReportStyle?.ReportType),
        telSysID: String(telSys),
        partitionIDs: partitions.join(),
        svcLvlAnsThres: String(callsAnsWithin || 0),
        callsInAnsThres1: String(callsInAnsThres1),
        callsInAnsThres2: String(callsInAnsThres2),
        callsInAnsThres3: String(callsInAnsThres3),
        callsInAnsThres4: String(callsInAnsThres4),
        callsInAnsThres5: String(callsInAnsThres5),
        callsInAnsThres6: String(callsInAnsThres6),
        svcLvlAbndThres: String(callsAnsWithin || 0),
        callsInAbndThres1: String(callsInAbndThres1),
        callsInAbndThres2: String(callsInAbndThres2),
        callsInAbndThres3: String(callsInAbndThres3),
        callsInAbndThres4: String(callsInAbndThres4),
        callsInAbndThres5: String(callsInAbndThres5),
        callsInAbndThres6: String(callsInAbndThres6),
        ACDNACode1: mapACDNACode(statisticSettingsACDNACodes, 1),
        ACDNACode2: mapACDNACode(statisticSettingsACDNACodes, 2),
        ACDNACode3: mapACDNACode(statisticSettingsACDNACodes, 3),
        ACDNACode4: mapACDNACode(statisticSettingsACDNACodes, 4),
        ACDNACode5: mapACDNACode(statisticSettingsACDNACodes, 5),
        ACDNACode6: mapACDNACode(statisticSettingsACDNACodes, 6),
        ACDNACode7: mapACDNACode(statisticSettingsACDNACodes, 7),
        ACDNACode8: mapACDNACode(statisticSettingsACDNACodes, 8),
        ACDNACode9: mapACDNACode(statisticSettingsACDNACodes, 9),
        ACDNACode10: mapACDNACode(statisticSettingsACDNACodes, 10),
        ACDNACode11: mapACDNACode(statisticSettingsACDNACodes, 11),
        ACDNACodeUseCodeDesc: String(useCodeDescriptionForACDNACodeStatisticSettings)
    })

    /**
     * Initialises report filter entity info data.
     */
    const initialiseRptFilterEntityInfo = async (): Promise<void> => {
        try {
            // Retrieve filter entity info.
            setFilterEntityInfoLoading(true)
            const filterEntityInfoResponse = await retrieveFilterEntityInfo(
                servletPath,
                telSys,
                partitions.join(),
                maxFilterListLimit
            )
            setFilterEntityInfo(filterEntityInfoResponse)
        } catch (error) {
            displayError(error as Error)
        } finally {
            setFilterEntityInfoLoading(false)
            reportLoaded.current = true
        }
    }

    /**
     * Updates currently configured statistics fields - if the field
     * is no longer available, we remove it, otherwise we check the field name and update it if needed.
     */
    const updateCurrentStatisticsConfigurations = (availableStatisticsFields: AvailableStatisticsFieldsArray): void => {
        // Update list of currently configured statistics configurations.
        setConfiguredStatisticsConfigurations(
            (previousStatisticsConfigurations: StatisticsConfiguration[]): StatisticsConfiguration[] => {
                // Iterate through all currently configured statistics.
                const mappedStatisticConfigurations: StatisticsConfiguration[] = []
                previousStatisticsConfigurations.forEach((statisticsConfiguration) => {
                    const newStatInfoObject: Definable<StatProps> = availableStatisticsFields.find(
                        (availableStatisticsField) => availableStatisticsField.StatID === statisticsConfiguration.ID
                    )

                    if (!newStatInfoObject) {
                        // If statistic is not in new list of available statistics, we do not keep it in list of configured statistics.
                    } else {
                        // Map the name, since this could have changed.
                        mappedStatisticConfigurations.push({
                            ...statisticsConfiguration,
                            mediumName: newStatInfoObject.MediumName
                        })
                    }
                })

                return mappedStatisticConfigurations
            }
        )
    }

    const reloadStatInfoAndUpdateCurrentStatisticsConfigurations = async (): Promise<void> => {
        // Retrieve stat info.
        try {
            setStatsInfoUpdateLoading(true)
            const availableStatisticsFields = await retrieveAvailableStatisticsFields(
                servletPath,
                prepareStatInfoRequestParamsFromCurrentState()
            )
            setCurrentlyAvailableStatisticsFields(availableStatisticsFields)
            // Reload statsList and then merge existing configurations and new statInfos
            updateCurrentStatisticsConfigurations(availableStatisticsFields)
        } catch (error) {
            displayError(error as Error)
        } finally {
            setStatsInfoUpdateLoading(false)
        }
    }

    useEffect(() => {
        /**
         * Reload statsList in case any value affecting the stat info request has been changed and update
         * currently configured statistics accordingly.
         */
        if (selectedReportStyle && !isDashboardReport) {
            reloadStatInfoAndUpdateCurrentStatisticsConfigurations()
        }
    }, [
        // selectedReportStyle,  // Change of this state variable is handled by handler method.
        telSys,
        partitions,
        callsAnsWithin,
        callsAbndWithin,
        callsInAnsThres1,
        callsInAnsThres2,
        callsInAnsThres3,
        callsInAnsThres4,
        callsInAnsThres5,
        callsInAnsThres6,
        callsInAbndThres1,
        callsInAbndThres2,
        callsInAbndThres3,
        callsInAbndThres4,
        callsInAbndThres5,
        callsInAbndThres6,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        statisticSettingsACDNACodes,
        useCodeDescriptionForACDNACodeStatisticSettings
    ])

    const mapCurrentStateToRptConfigurationDataDto = (): RptConfigurationDataRequestDto | undefined => {
        if (!selectedReportStyle) {
            return undefined
        }

        const statisticsDisplayValue = isDashboardReport
            ? mapConfiguredDashboardReportsToStatisticsDisplayString(
                  configuredDashboardReports,
                  selectedDashboardLayoutStyle
              )
            : mapStatisticsConfigurationsToRequestStatisticsDisplayString(configuredStatisticsConfigurations)

        const statisticsSettingsValue = isDashboardReport
            ? mapDashboardLayoutStyleToStatisticsSettingsString(selectedDashboardLayoutStyle)
            : mapStatisticsConfigurationsToRequestStatisticsSettingsString(
                  configuredStatisticsConfigurations,
                  currencyFormatter
              )

        const zoomLevelValue = parseInt(zoomLevel, 10) ? parseInt(zoomLevel, 10) : ZOOM_LEVEL_DEFAULT_VALUE
        const zoomLevelRequestValue = isZoomLevelCustom ? zoomLevelValue : ZOOM_LEVEL_VALUE_AUTO

        const dto: RptConfigurationDataRequestDto = {
            // IsDescriptionChanged: boolean // Not used by BE logic in case of XML / JSON response, we can skip this parameter.
            // IsSetCallsInAnsThresChanged: boolean // Not used by BE logic in case of XML / JSON response, we can skip this parameter.
            Description: prepareReportDescriptionForAddUpdateRequest(folderPath, reportName),
            Information: note,
            Type: selectedReportStyle.ReportType,
            // StatisticsContent:   // Not needed in update request.
            StatisticsDisplay: statisticsDisplayValue,
            StatisticsSettings: statisticsSettingsValue,
            // MethodsGroupedBy: // Not needed in update request.
            // DateTimeBinned: // Not needed in update request.
            // StatisticsOrderedBy: // Not needed in update request.
            // StatisticsOrderedByAscOrDesc: // Not needed in update request.
            Duration: dateTimeOptionSelected,
            // DurationIsUTC: boolean  // This is always false in current UI, which is the default value on backend - we skip this parameter for now.
            DurationCustomStart: convertDateToDurationDateTimeString(reportDateTimeRangeStart, language),
            DurationCustomEnd: convertDateToDurationDateTimeString(reportDateTimeRangeEnd, language),
            DurationTimeOfDayStart: convertDateToDurationTimeString(reportTimeRangeStart),
            DurationTimeOfDayEnd: convertDateToDurationTimeString(reportTimeRangeEnd),
            DurationDaysOfWeek: mapDaysStatePropertyToDaysOfWeekBitmap(reportDateTimeDaysOfTheWeek, language),
            DurationDayCount: typeof reportDateTimeLastXDays === 'number' ? reportDateTimeLastXDays : undefined,

            // FilterIDDb: // Not needed in update request.
            // FilterIDTimeSync: // Not needed in update request.
            FilterCallIDThread: callSegmentChainID,
            // FilterCallIDThreadWCMatches: true, // This is not configurable property - let the backend use the default value.
            FilterCallIDTelSys: callIdentifier,
            // FilterCallIDTelSysWCMatches: true, // This is not configurable property - let the backend use the default value.
            FilterTelephoneSystemID: telSys,
            FilterPartitionIDs: partitions.join(','),
            FilterTelNos: telephoneNos,
            FilterTelNoDescriptions: telephoneNoDescriptions,
            FilterDevices: devices.replace(/,/g, '\u00B8'),
            FilterDeviceTypes: deviceTypes.map((deviceType: number): string => `${deviceType}`).join(','),
            FilterDevicesIfGrpIncGrpMmbrCalls: includeCallsForGrpDev,
            FilterDevicesIfGrpIncACDItemsForGrpDevOnly: calcACDStatsForAgntsSignedIntoSpecGrp,
            FilterACDAgents: ACDAgents,
            FilterLocation: filterLocationToFrom,
            FilterCallType: callTypeDirection,
            FilterCallTypeConfCallAsExternal: treatConfCallAsExternal,
            FilterCallChannels: mapStateChannelTypesValuesToRequestFilterChannels(channelTypes),
            FilterSubject: subjectFilter,
            FilterAnsType: answerType,
            FilterCLIType: callerID,
            FilterDDIDigits: DDIDigits,
            FilterAccountCodes: accountCodes,
            FilterMovedToTelNos: telnosMovedTo,
            FilterMovedFromDevices: devicesMovedFrom,
            FilterMovedFromACDAgents: ACDAgentsMovedFrom,
            FilterMovedSegNo: callSegmentNumber,
            FilterMovedSegReasonStarted: callSegmentStartReason,
            FilterMovedSegReasonEnded: callSegmentEndReason,
            FilterCallTimeRngAllCmp: ringTimeDistComparisonType,
            FilterCallTimeRngAllValue1Text: ringTimeDistValue1,
            FilterCallTimeRngAllValue2Text: ringTimeDistValue2,
            FilterCallTimeRngSegCmp: ringTimeSegmentComparisonType,
            FilterCallTimeRngSegValue1Text: ringTimeSegmentValue1,
            FilterCallTimeRngSegValue2Text: ringTimeSegmentValue2,
            FilterCallTimeTalkCmp: callTalkTimeComparisonType,
            FilterCallTimeTalkValue1Text: callTalkTimeValue1,
            FilterCallTimeTalkValue2Text: callTalkTimeValue2,
            FilterCallTimeAllCmp: totalCallTimeComparisonType,
            FilterCallTimeAllValue1Text: totalCallTimeValue1,
            FilterCallTimeAllValue2Text: totalCallTimeValue2,
            FilterCallTimeHeldCmp: callHeldTimeComparisonType,
            FilterCallTimeHeldValue1Text: callHeldTimeValue1,
            FilterCallTimeHeldValue2Text: callHeldTimeValue2,
            StatisticSettingACDNACode01: mapACDNACode(statisticSettingsACDNACodes, 1),
            StatisticSettingACDNACode02: mapACDNACode(statisticSettingsACDNACodes, 2),
            StatisticSettingACDNACode03: mapACDNACode(statisticSettingsACDNACodes, 3),
            StatisticSettingACDNACode04: mapACDNACode(statisticSettingsACDNACodes, 4),
            StatisticSettingACDNACode05: mapACDNACode(statisticSettingsACDNACodes, 5),
            StatisticSettingACDNACode06: mapACDNACode(statisticSettingsACDNACodes, 6),
            StatisticSettingACDNACode07: mapACDNACode(statisticSettingsACDNACodes, 7),
            StatisticSettingACDNACode08: mapACDNACode(statisticSettingsACDNACodes, 8),
            StatisticSettingACDNACode09: mapACDNACode(statisticSettingsACDNACodes, 9),
            StatisticSettingACDNACode10: mapACDNACode(statisticSettingsACDNACodes, 10),
            StatisticSettingACDNACode11: mapACDNACode(statisticSettingsACDNACodes, 11),
            StatisticSettingACDNACodeUseCodeDesc: useCodeDescriptionForACDNACodeStatisticSettings,
            StatisticSettingSvcLvlAnsThres: callsAnsWithin,
            StatisticSettingSvcLvlAbndThres: callsAbndWithin,
            StatisticSettingSvcLvlIgnQckAbndCalls: isSrvLvlIgnQuickAbndCallsWithinEnabled,
            StatisticSettingSvcLvlQckAbndCallsThres: srvLvlIgnQuickAbndCallsWithin,
            StatisticSettingSvcLvlIgnOvrfldCalls: isSrvLvlIgnOvrflwdCallsWithinEnabled,
            StatisticSettingSvcLvlOvrfldCallsThres: srvLvlIgnOvrflwdCallsWithin,
            StatisticSettingUnreturnedLostCallsIncludeEndedOnHold: includeEndedOnHold,
            StatisticSettingUnreturnedLostCallsIncludeEscaped: includeEscaped,
            StatisticSettingExcludeHeldTimeFromTalkTime: excludeHeldTime,
            StatisticSettingCallsInIgnQckAbndCalls: isInbStIgnQuickAbndCallsWithinEnabled,
            StatisticSettingCallsInQckAbndCallsThres: inbStIgnQuickAbndCallsWithin,
            StatisticSettingCallsInIgnOvrfldCalls: isInbStIgnOvrflwdCallsWithinEnabled,
            StatisticSettingCallsInOvrfldCallsThres: inbStIgnOvrflwdCallsWithin,
            StatisticSettingCallsInAnsThres1: callsInAnsThres1,
            StatisticSettingCallsInAnsThres2: callsInAnsThres2,
            StatisticSettingCallsInAnsThres3: callsInAnsThres3,
            StatisticSettingCallsInAnsThres4: callsInAnsThres4,
            StatisticSettingCallsInAnsThres5: callsInAnsThres5,
            StatisticSettingCallsInAnsThres6: callsInAnsThres6,
            StatisticSettingCallsInAbndThres1: callsInAbndThres1,
            StatisticSettingCallsInAbndThres2: callsInAbndThres2,
            StatisticSettingCallsInAbndThres3: callsInAbndThres3,
            StatisticSettingCallsInAbndThres4: callsInAbndThres4,
            StatisticSettingCallsInAbndThres5: callsInAbndThres5,
            StatisticSettingCallsInAbndThres6: callsInAbndThres6,
            StatisticSettingFinancialOrderValueInbound: monetaryCallAssignmentsValues.inboundCallsAvgOrderVal,
            StatisticSettingFinancialOrderValueOutbound: monetaryCallAssignmentsValues.outboundCallsAvgOrderVal,
            StatisticSettingFinancialStrikeRateInbound: monetaryCallAssignmentsValues.inboundStrikeRate,
            StatisticSettingFinancialStrikeRateOutbound: monetaryCallAssignmentsValues.outboundStrikeRate,
            StatisticSettingFinancialCurrencyID: currencyDisplayFormatSettings.currencyId,
            StatisticSettingFinancialCurrencyFormatPattern: currencyDisplayFormatSettings.pattern,
            StatisticSettingFinancialCurrencyGroupingSeparator: mapFinancialGroupingSeparatorToStringValue(
                currencyDisplayFormatSettings.groupingSeparator
            ),
            StatisticSettingFinancialCurrencyDecimalSeparator: mapFinancialDecimalSeparatorToStringValue(
                currencyDisplayFormatSettings.decimalSeparator
            ),
            StatisticSettingMvdToUnResDevsAreOvrfldCalls: considerCallsMvdToUnresAsOverflwd,
            DebugContentDump: enableRptRequestAudit,
            MaxRows: maxRptRowDisplLimit,
            MaxFilterEntities: maxFilterListLimit,
            AlarmACDAvailableOn: isOnAvailableStateEnabled,
            AlarmACDAvailableThres: convertDurationAsFormattedStringToSeconds(onAvailableState, 30),
            AlarmACDBusyOn: isOnBusyStateEnabled,
            AlarmACDBusyThres: convertDurationAsFormattedStringToSeconds(onBusyState, 300),
            AlarmACDNADNDOn: isOnNAOrDNDStateEnabled,
            AlarmACDNADNDThres: convertDurationAsFormattedStringToSeconds(onNAOrDNDState, 180),
            AlarmACDWrapUpOn: isOnWrapupStateEnabled,
            AlarmACDWrapUpThres: convertDurationAsFormattedStringToSeconds(onWrapupState, 180),
            AlarmCallAlertOn: isOnInbAlertingEnabled,
            AlarmCallAlertThres: convertDurationAsFormattedStringToSeconds(onInbAlerting, 15),
            AlarmCallTalkOn: isOnTalkTimeEnabled,
            AlarmCallTalkThres: convertDurationAsFormattedStringToSeconds(onTalkTime, 300),
            AlarmCallLostOn: alarmOnNewAbandoned,
            AlarmFirePlaySound: playSound,
            AlarmFireActivateWin: activeWindow,
            ZoomLevel: zoomLevelRequestValue,
            ReportViewType: displayedAsListReport,
            ReportViewFlags: mapStatePropertiesToReportViewFlags(
                isShowingHeaderRowEnabled,
                isShowingFooterRowEnabled,
                isShowingContentRowsEnabled,
                isShowingSpacerRowsEnabled,
                isShowingLargeBLFToolTipsEnabled,
                isAutomaticTextTruncationEnabled
            ),
            PrintViewChartInclusion: printViewChartInclusion,
            PrintViewFlags: mapStatePropertiesToPrintViewFlags(
                isPrintShowingHeaderRowEnabled,
                isPrintShowingFooterRowEnabled,
                isPrintShowingContentRowsEnabled,
                isPrintShowingSpacerRowsEnabled,
                isPrintShowSummaryHeaderEnabled
            ),
            ChartProvider: chartProvider,
            ChartType: chartType,
            WallboardTileType: displayedAsWallboardReport,
            IsAutoSwitched: includeInTransitioningSequence,
            AutoSwitchIntervalDisplayText: rptDisplIntervalTime,
            // AutoSwitchIntervalDisplayRealValue: convertDurationAsFormattedStringToSeconds(rptDisplIntervalTime, 5) * 1000, // Real Value is not used by BE
            ExternalContentURL: externalURL,
            FormatCSVStatValuesType: formatCSVStatValuesType
            // GroupingViewType: // Not needed in update request.
            // UserLicensedFor: // Not needed in update request.
            // IsLicensed: // Not needed in update request.
            // IsLicensedRealTime: // Not needed in update request.
            // SupportsIconViews: // Not needed in update request.
            // SupportsCharting: isReportChartingFeatureEnabled, // Not needed in update request.
            // SupportsRowHL: // Not needed in update request.
            // DurationFilterSummaryText: // Not needed in update request.
        }

        // if (scheduleReportOn !== SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE) {
        // Always populate Schedule properties - it works the same in old JSP UI.
        const scheduleDto: RptConfigurationDataUpdateScheduleDto = {
            ScheduleDelete: deleteSchedule,
            ScheduleReset: resetSchedule,
            ScheduleLocaleStr: useLanguage,
            SchedulePaperSizeID: paperSize,
            ScheduleFormat: targetRptFormat,
            ScheduleType: scheduleReportOn,
            ScheduleAtDate: runOnDate ? convertDateToDateString(runOnDate, language) : '',
            ScheduleAtDayOfWeek: mapDaysStateStringValueToDayApiValue(runOnDayInWeek, language),
            ScheduleAtDayOfWeekFilter: mapDaysStatePropertyToDaysOfWeekBitmap(runOnDays, language),
            ScheduleAtDayOfMonth: runOnDayInMonth,
            // ScheduleAtMonthOfYear: runOn // Not supported currently by old UI.
            ScheduleAtTime: convertDateToDurationTimeString(runAtTime),
            ScheduleAtHourFilterStart: runBetweenStart.getHours(),
            ScheduleAtHourFilterEnd: runBetweenEnd.getHours(),
            ScheduleAtMinute: runAtMinute.getMinutes(),
            ScheduleAutoDuration: autoCalcDuration,
            ScheduleBundleAsZIP: bundleEmailAttachAsZip,
            ScheduleMergeWithOtherSchedules: runWithSimilarSchedules
            // ScheduleStatusDateTimeStart: string  // Not needed.
            // ScheduleStatusDateTimeNextPure: string // Not needed.
            // ScheduleStatusDateTimeNextActual: string // Not needed.
            // ScheduleStatusDateTimeLastPure: string // Not needed.
            // ScheduleStatusDateTimeLastActual: string // Not needed.
            // ScheduleStatusMessage: string // Not needed.
            // ScheduleStatusFailed: string // Not needed.
        }

        dto.schedule = scheduleDto

        if (
            selectedReportStyle?.ReportType === ReportTypes.TOTAL_CALL_BY_INTERVAL ||
            selectedReportStyle?.ReportType === ReportTypes.CALL_BY_INTERVAL
        ) {
            dto.CustomIntervalValue =
                selectedReportStyle?.ReportType === ReportTypes.TOTAL_CALL_BY_INTERVAL ||
                (selectedReportStyle?.ReportType === ReportTypes.CALL_BY_INTERVAL &&
                    (callByIntervalType === CallIntervalTypes.MINUTES ||
                        callByIntervalType === CallIntervalTypes.HOURS))
                    ? totalCallByInterval
                    : REPORT_CALLS_BY_ONE

            dto.CustomIntervalType = callByIntervalType
        }

        return dto
    }

    const mapCurrentStateToAddReportDto = (): RptConfigurationDataAddDto | undefined => {
        const dto = mapCurrentStateToRptConfigurationDataDto()
        return dto
    }

    const mapCurrentStateToUpdateReportConfigurationDto = (): RptConfigurationDataUpdateDto | undefined => {
        if (!reportID) {
            return undefined
        }

        const baseDto = mapCurrentStateToRptConfigurationDataDto()
        if (baseDto) {
            return {
                ...baseDto,
                ID: reportID
            }
        }
        return undefined
    }

    const submitReportConfiguration = async (): Promise<void> => {
        try {
            setIsSubmitting(true)
            if (reportID) {
                const dto = mapCurrentStateToUpdateReportConfigurationDto()

                if (dto) {
                    const responseDto = await updateReportConfiguration(servletPath, dto)

                    if (configuredDashboardReports?.length > 0) {
                        // eslint-disable-next-line
                        com?.Akixi?.EmptyAllWrappers()
                    }

                    handleReportUpdateRenameActiveRptDescription(
                        reportID,
                        responseDto.Description,
                        responseDto.DurationFilterSummaryText
                    )
                    // Reload data for report list in main menu.
                    fetchReportList(reportID, responseDto.Description).catch(displayError)
                }
            } else {
                const dto = mapCurrentStateToAddReportDto()

                if (dto) {
                    const response = await addReport(servletPath, dto)
                    // In case of update modal, this method is not provided.
                    if (refreshReportListDataAndStartReport)
                        refreshReportListDataAndStartReport(response.ID, response.Description)

                    const newReport: NewReport = {
                        reportID: response.ID,
                        reportDesc: response.Description,
                        uuid
                    }

                    if (popoutMode) {
                        const openerWindowRef$: AkixiWindow = window.opener as AkixiWindow
                        // eslint-disable-next-line
                        openerWindowRef$?.rxjs?.newReportCreated?.announce(newReport)
                    } else {
                        newReportCreated$?.announce(newReport)
                    }
                }
            }

            if (popoutMode) {
                window.close()
            } else {
                closeModal()
            }
        } catch (error) {
            displayError(error as Error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancelModal = (): void => {
        displayExitPrompt()
    }

    /**
     * Loads available reports list and updates respective state props.
     */
    const loadReportList = async (): Promise<void> => {
        try {
            // Retrieve list of reports from API.
            setReportListLoading(true)
            const reportListResponse = await retrieveReportList(ReportTypes.DASHBOARD)

            if (reportListResponse) {
                if (reportID) {
                    // If this is an modify dialog, the current report should not be present in the list of available report list items.
                    setAvailableReportListItems(
                        toReportListItems(
                            reportListResponse.ReportList.filter(
                                (reportListItem) => reportListItem.ReportID !== reportID
                            )
                        )
                    )
                } else {
                    setAvailableReportListItems(toReportListItems(reportListResponse.ReportList))
                }
            }
        } catch (error) {
            displayError(error as Error)
        } finally {
            setReportListLoading(false)
        }
    }

    useEffect(() => {
        /**
         * Loads list of available reports (only needed for Dashboard report).
         */
        if (isDashboardReport) {
            loadReportList()
        }
    }, [selectedReportStyle])

    /**
     * Method that fetches fresh currentlyAvailableStatisticsFields and sets current statistics configuration based on the strings provided.
     *
     * @param statisticsDisplay Report property (formatted string).
     * @param statisticsSettings Report property (formatted string).
     * @param retrieveStatInfoParameters Object with report properties required for currentlyAvailableStatisticsFields request.
     * @param currencyFormatterToUse Currency formatter object to be used when formatting financial values.
     */
    const reloadStatInfoAndSetStatisticsConfigurations = async (
        statisticsDisplay: string,
        statisticsSettings: string,
        retrieveStatInfoParameters: RetrieveStatInfoRequestParameters,
        currencyFormatterToUse: CDecimalFormatter
    ): Promise<void> => {
        try {
            setStatsInfoLoading(true)
            // Retrieve up-to-date list of available statistics.
            const statInfoResponse = await retrieveAvailableStatisticsFields(servletPath, retrieveStatInfoParameters)

            // Map statistics initial configuration strings to statisticsConfigurations list
            const statisticConfigurations: StatisticsConfiguration[] =
                mapStatisticsDisplayAndStatisticsStringToStatsConfigs(
                    statisticsDisplay,
                    statisticsSettings,
                    statInfoResponse,
                    percentageFormatString,
                    language,
                    currencyFormatterToUse
                )
            // Replace list of currently configured statistics configurations completely.
            setCurrentlyAvailableStatisticsFields(statInfoResponse)
            setConfiguredStatisticsConfigurations(statisticConfigurations)
            setHasStatConfigurationsLoaded(true)
            setDisablePopoutOption(false)
        } catch (error) {
            displayError(error as Error)
        } finally {
            setStatsInfoLoading(false)
        }
    }

    /**
     * Retrieves report configuration of existing report.
     */
    const loadExistingReportConfiguration = async (
        existingReportId: string
    ): Promise<RptConfigurationDataResponseDto> => {
        const rptConfigurationData: Voidable<RptConfigurationDataResponseDto> = await retrieveReportConfigurationData(
            servletPath,
            existingReportId
        ).catch((e: InternalAPIError) => {
            closeModal()
            throw e
        })

        return rptConfigurationData
    }

    /**
     * Retrieves report configuration of existing report and updates all report properties in state.
     *
     * @param reportDefinitions Available report definitions.
     * @param rptConfigurationDataResponseDto Report configuration.
     */
    const mapReportConfigurationAndUpdateState = (
        rptConfigurationDataResponseDto: RptConfigurationDataResponseDto,
        reportPropertiesResponse: ReportDefs
    ): void => {
        if (reportID) {
            const reportType = rptConfigurationDataResponseDto.Type
            const selectedReportStyleByReportType = reportPropertiesResponse?.find(
                (reportDef) => reportDef.ReportType === reportType
            )
            setSelectedReportStyle(selectedReportStyleByReportType)
            // Remember what reportStyle was set when this existing report was first loaded.
            setReportModifyOriginalReportStyleReportType(selectedReportStyleByReportType?.ReportType)

            setReportName(getReportNameFromReportDescription(rptConfigurationDataResponseDto.Description))
            setTotalCallByInterval(
                rptConfigurationDataResponseDto.CustomIntervalValue ?? REPORT_CALLS_BY_INTERVAL_30_MINUTES_VALUE
            )
            setCallByIntervalType(rptConfigurationDataResponseDto.CustomIntervalType ?? CallIntervalTypes.MINUTES)
            setFolderPath(getFolderFromReportDescription(rptConfigurationDataResponseDto.Description))
            setNote(rptConfigurationDataResponseDto.Information)

            // displayed as
            setDisplayedAsListReport(rptConfigurationDataResponseDto.ReportViewType)
            setDisplayedAsWallBoardReport(rptConfigurationDataResponseDto.WallboardTileType)

            // zoom level
            if (rptConfigurationDataResponseDto.ZoomLevel === ZOOM_LEVEL_VALUE_AUTO) {
                setIsZoomLevelCustom(false)
                setZoomLevel('')
            } else {
                setZoomLevel(rptConfigurationDataResponseDto.ZoomLevel.toString())
                setIsZoomLevelCustom(true)
            }

            // updates to separate properties should be done via useEffect
            setIsShowingHeaderRowEnabled(mapResponseToShowingHeaderRowEnabled(rptConfigurationDataResponseDto))
            setIsShowingFooterRowEnabled(mapResponseToShowingFooterRowEnabled(rptConfigurationDataResponseDto))
            setIsShowingContentRowsEnabled(mapResponseToShowingContentRowsEnabled(rptConfigurationDataResponseDto))
            setIsShowingSpacerRowsEnabled(mapResponseToShowingSpacerRowsEnabled(rptConfigurationDataResponseDto))
            setIsShowingLargeBLFToolTipsEnabled(
                mapResponseToShowingLargeBLFToolTipsEnabled(rptConfigurationDataResponseDto)
            )
            setIsAutomaticTextTruncationEnabled(
                mapResponseToAutomaticTextTruncationEnabled(rptConfigurationDataResponseDto)
            )

            setChartProvider(rptConfigurationDataResponseDto.ChartProvider)
            setChartType(rptConfigurationDataResponseDto.ChartType)

            setExternalURL(rptConfigurationDataResponseDto.ExternalContentURL)

            const duration: number = rptConfigurationDataResponseDto.Duration
            setDateTimeOptionSelected(duration)

            setReportTimeRangeStart(
                convertDurationTimeStringToDate(rptConfigurationDataResponseDto.DurationTimeOfDayStart)
            )
            setReportTimeRangeEnd(convertDurationTimeStringToDate(rptConfigurationDataResponseDto.DurationTimeOfDayEnd))
            setReportDateTimeRangeStart(new Date(rptConfigurationDataResponseDto.DurationCustomStart))
            setReportDateTimeRangeEnd(new Date(rptConfigurationDataResponseDto.DurationCustomEnd))
            setReportDateTimeDaysOfTheWeek(
                mapResponseDaysOfTheWeekToDaysOfWeekArray(rptConfigurationDataResponseDto.DurationDaysOfWeek, language)
            )
            setReportDateTimeLastXDays(rptConfigurationDataResponseDto.DurationDayCount)

            // alarm tab properties
            setIsOnAvailableStateEnabled(rptConfigurationDataResponseDto.AlarmACDAvailableOn)
            setOnAvailableState(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.AlarmACDAvailableThres,
                    language
                )
            )

            setIsOnBusyStateEnabled(rptConfigurationDataResponseDto.AlarmACDBusyOn)
            setOnBusyState(
                convertDurationInSecondsToFormattedString(rptConfigurationDataResponseDto.AlarmACDBusyThres, language)
            )

            setIsOnNAOrDNDStateEnabled(rptConfigurationDataResponseDto.AlarmACDNADNDOn)
            setOnNAOrDNDState(
                convertDurationInSecondsToFormattedString(rptConfigurationDataResponseDto.AlarmACDNADNDThres, language)
            )

            setIsOnWrapupStateEnabled(rptConfigurationDataResponseDto.AlarmACDWrapUpOn)
            setOnWrapupState(
                convertDurationInSecondsToFormattedString(rptConfigurationDataResponseDto.AlarmACDWrapUpThres, language)
            )

            setIsOnInbAlertingEnabled(rptConfigurationDataResponseDto.AlarmCallAlertOn)
            setOnInbAlerting(
                convertDurationInSecondsToFormattedString(rptConfigurationDataResponseDto.AlarmCallAlertThres, language)
            )

            setIsOnTalkTimeEnabled(rptConfigurationDataResponseDto.AlarmCallTalkOn)
            setOnTalkTime(
                convertDurationInSecondsToFormattedString(rptConfigurationDataResponseDto.AlarmCallTalkThres, language)
            )

            setAlarmOnNewAbandoned(rptConfigurationDataResponseDto.AlarmCallLostOn)
            setPlaySound(rptConfigurationDataResponseDto.AlarmFirePlaySound)
            setActiveWindow(rptConfigurationDataResponseDto.AlarmFireActivateWin)

            // // print tab
            setPrintViewChartInclusion(rptConfigurationDataResponseDto.PrintViewChartInclusion)
            setIsPrintShowingHeaderRowEnabled(
                mapResponseToIsPrintShowingHeaderRowEnabled(rptConfigurationDataResponseDto)
            )
            setIsPrintShowingFooterRowEnabled(
                mapResponseToIsPrintShowingFooterRowEnabled(rptConfigurationDataResponseDto)
            )
            setIsPrintShowingContentRowsEnabled(
                mapResponseToIsPrintShowingContentRowsEnabled(rptConfigurationDataResponseDto)
            )
            setIsPrintShowingSpacerRowsEnabled(
                mapResponseToIsPrintShowingSpacerRowsEnabled(rptConfigurationDataResponseDto)
            )
            setIsPrintShowSummaryHeaderEnabled(mapResponseToIsShowSummaryHeaderEnabled(rptConfigurationDataResponseDto))

            setIsReportChartingFeatureEnabled(rptConfigurationDataResponseDto.SupportsCharting)

            // // Advanced tab properties
            setIncludeCallsForGrpDev(rptConfigurationDataResponseDto.FilterDevicesIfGrpIncGrpMmbrCalls)
            setCalcACDStatsForAgntsSignedIntoSpecGrp(
                rptConfigurationDataResponseDto.FilterDevicesIfGrpIncACDItemsForGrpDevOnly
            )
            setConsiderCallsMvdToUnresAsOverflwd(
                rptConfigurationDataResponseDto.StatisticSettingMvdToUnResDevsAreOvrfldCalls
            )
            setMaxFilterListLimit(rptConfigurationDataResponseDto.MaxFilterEntities)
            setMaxRptRowDisplLimit(rptConfigurationDataResponseDto.MaxRows)
            setFormatCSVStatValuesType(rptConfigurationDataResponseDto.FormatCSVStatValuesType)
            setEnableRptRequestAudit(rptConfigurationDataResponseDto.DebugContentDump)

            // Scheduling tab properties.
            if (rptConfigurationDataResponseDto.Schedule) {
                const schedule = rptConfigurationDataResponseDto.Schedule
                setIsScheduleActive(schedule.IsActive)
                setScheduleReportOn(schedule.Type)
                setRunOnDate(new Date(schedule.AtDate))
                setRunAtTime(convertDurationTimeStringToDate(schedule.AtTime))

                const atDayOfWeekFilter = schedule.AtDayOfWeekFilter
                setRunOnDays(mapResponseDaysOfTheWeekToDaysOfWeekArray(atDayOfWeekFilter, language))
                const atMinute = new Date()
                atMinute.setMinutes(schedule.AtMinute)
                setRunAtMinute(atMinute)

                setRunOnDayInWeek(mapResponseDayOfTheWeekToDayOfWeekString(schedule.AtDayOfWeek, language))
                setRunOnDayInMonth(schedule.AtDayOfMonth)

                // when implementing mapping from state to request, only use .getHours()
                const atHourStart = new Date()
                atHourStart.setHours(schedule.AtHourFilterStart)
                setRunBetweenStart(atHourStart)
                const atHourEnd = new Date()
                atHourEnd.setHours(schedule.AtHourFilterEnd)
                setRunBetweenEnd(atHourEnd)

                setTargetRptFormat(schedule.Format)
                setUseLanguage(schedule.LocaleStr)
                setPaperSize(schedule.PaperSizeID)

                setAutoCalcDuration(schedule.AutoDuration)
                setBundleEmailAttachAsZip(schedule.BundleAsZIP)
                setRunWithSimilarSchedules(schedule.MergeWithOtherSchedules)

                setScheduleNextRunningAt(schedule.StatusDateTimeNext)
                setScheduleLastRunningAt(schedule.StatusDateTimeLastActual)
                setScheduleStatusError(schedule.StatusMessage)
            } else {
                setScheduleReportOn(SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE)
            }

            // Settings tab properties.
            setIncludeInTransitioningSequence(rptConfigurationDataResponseDto.IsAutoSwitched)
            // AutoSwitchIntervalDisplay is returned as milliseconds, but we need to present it in seconds.
            setRptDisplIntervalTime(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.AutoSwitchIntervalDisplay / 1000,
                    language
                )
            )

            // SLA/KPI tab properties.
            setCallsAnsWithin(rptConfigurationDataResponseDto.StatisticSettingSvcLvlAnsThres)
            setCallsAbndWithin(rptConfigurationDataResponseDto.StatisticSettingSvcLvlAbndThres)
            setIsSrvLvlIgnQuickAbndCallsWithinEnabled(
                rptConfigurationDataResponseDto.StatisticSettingSvcLvlIgnQckAbndCalls
            )
            setSrvLvlIgnQuickAbndCallsWithin(rptConfigurationDataResponseDto.StatisticSettingSvcLvlQckAbndCallsThres)

            setIsSrvLvlIgnOvrflwdCallsWithinEnabled(
                rptConfigurationDataResponseDto.StatisticSettingSvcLvlIgnOvrfldCalls
            )
            setSrvLvlIgnOvrflwdCallsWithin(rptConfigurationDataResponseDto.StatisticSettingSvcLvlOvrfldCallsThres)

            setIsInbStIgnQuickAbndCallsWithinEnabled(
                rptConfigurationDataResponseDto.StatisticSettingCallsInIgnQckAbndCalls
            )
            setInbStIgnQuickAbndCallsWithin(rptConfigurationDataResponseDto.StatisticSettingCallsInQckAbndCallsThres)

            setIsInbStIgnOvrflwdCallsWithinEnabled(
                rptConfigurationDataResponseDto.StatisticSettingCallsInIgnOvrfldCalls
            )
            setInbStIgnOvrflwdCallsWithin(rptConfigurationDataResponseDto.StatisticSettingCallsInOvrfldCallsThres)

            setCallsInAnsThres1(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres1)
            setCallsInAnsThres2(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres2)
            setCallsInAnsThres3(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres3)
            setCallsInAnsThres4(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres4)
            setCallsInAnsThres5(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres5)
            setCallsInAnsThres6(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres6)
            setCallsInAbndThres1(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres1)
            setCallsInAbndThres2(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres2)
            setCallsInAbndThres3(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres3)
            setCallsInAbndThres4(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres4)
            setCallsInAbndThres5(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres5)
            setCallsInAbndThres6(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres6)

            setIncludeEndedOnHold(rptConfigurationDataResponseDto.StatisticSettingUnreturnedLostCallsIncludeEndedOnHold)
            setIncludeEscaped(rptConfigurationDataResponseDto.StatisticSettingUnreturnedLostCallsIncludeEscaped)
            setExcludeHeldTime(rptConfigurationDataResponseDto.StatisticSettingExcludeHeldTimeFromTalkTime)

            // Filter tab properties.
            const deviceFilterString: string = getEncodedDeviceFilterString(
                rptConfigurationDataResponseDto.FilterDevices
            )
            setDeviceTypes(
                rptConfigurationDataResponseDto.FilterDeviceTypes?.split(',').map((deviceType: string) =>
                    parseInt(deviceType, 10)
                ) ?? [ALL_VALUE]
            )
            setTelSys(rptConfigurationDataResponseDto.FilterTelephoneSystemID)
            setReportModifyOriginalTelSys(rptConfigurationDataResponseDto.FilterTelephoneSystemID)
            if (rptConfigurationDataResponseDto.FilterPartitionIDs) {
                // Only set partitions value if the provided partition filter is not empty, otherwise keep the default.
                setPartitions(rptConfigurationDataResponseDto.FilterPartitionIDs.split(','))
                setReportModifyOriginalPartitions(rptConfigurationDataResponseDto.FilterPartitionIDs.split(','))
            }

            setTelephoneNos(rptConfigurationDataResponseDto.FilterTelNos || ANY_VALUE_STRING_DEFAULT)
            setTelephoneNoDescriptions(
                rptConfigurationDataResponseDto.FilterTelNoDescriptions || ANY_VALUE_STRING_DEFAULT
            )
            setDevices(deviceFilterString || ANY_VALUE_STRING_DEFAULT)
            setACDAgents(rptConfigurationDataResponseDto.FilterACDAgents || ANY_VALUE_STRING_DEFAULT)
            setFilterLocationToFrom(rptConfigurationDataResponseDto.FilterLocation)

            setChannelTypes(
                mapResponseFilterCallChannelsBitmapToChannelTypes(rptConfigurationDataResponseDto.FilterCallChannels)
            )
            setSubjectFilter(rptConfigurationDataResponseDto.FilterSubject)
            setCallTypeDirection(rptConfigurationDataResponseDto.FilterCallType)
            setAnswerType(rptConfigurationDataResponseDto.FilterAnsType)
            setCallerID(rptConfigurationDataResponseDto.FilterCLIType)
            setDDIDigits(rptConfigurationDataResponseDto.FilterDDIDigits || ANY_VALUE_STRING_DEFAULT)
            setAccountCodes(rptConfigurationDataResponseDto.FilterAccountCodes || ANY_VALUE_STRING_DEFAULT)
            setCallIdentifier(rptConfigurationDataResponseDto.FilterCallIDTelSys || ANY_VALUE_STRING_DEFAULT)
            setTreatConfCallAsExternal(rptConfigurationDataResponseDto.FilterCallTypeConfCallAsExternal)

            // "Other" tab properties.
            setTelnosMovedTo(rptConfigurationDataResponseDto.FilterMovedToTelNos || ANY_VALUE_STRING_DEFAULT)
            setDevicesMovedFrom(rptConfigurationDataResponseDto.FilterMovedFromDevices || ANY_VALUE_STRING_DEFAULT)
            setACDAgentsMovedFrom(rptConfigurationDataResponseDto.FilterMovedFromACDAgents || ANY_VALUE_STRING_DEFAULT)
            setCallSegmentNumber(rptConfigurationDataResponseDto.FilterMovedSegNo)
            setCallSegmentStartReason(rptConfigurationDataResponseDto.FilterMovedSegReasonStarted)
            setCallSegmentEndReason(rptConfigurationDataResponseDto.FilterMovedSegReasonEnded)
            setCallSegmentChainID(rptConfigurationDataResponseDto.FilterCallIDThread || ANY_VALUE_STRING_DEFAULT)
            setRingTimeDistComparisonType(rptConfigurationDataResponseDto.FilterCallTimeRngAllCmp)
            setRingTimeDistValue1(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeRngAllValue1,
                    language
                )
            )
            setRingTimeDistValue2(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeRngAllValue2,
                    language
                )
            )
            setRingTimeSegmentComparisonType(rptConfigurationDataResponseDto.FilterCallTimeRngSegCmp)
            setRingTimeSegmentValue1(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeRngSegValue1,
                    language
                )
            )
            setRingTimeSegmentValue2(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeRngSegValue2,
                    language
                )
            )
            setCallTalkTimeComparisonType(rptConfigurationDataResponseDto.FilterCallTimeTalkCmp)
            setCallTalkTimeValue1(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeTalkValue1,
                    language
                )
            )
            setCallTalkTimeValue2(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeTalkValue2,
                    language
                )
            )
            setTotalCallTimeComparisonType(rptConfigurationDataResponseDto.FilterCallTimeAllCmp)
            setTotalCallTimeValue1(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeAllValue1,
                    language
                )
            )
            setTotalCallTimeValue2(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeAllValue2,
                    language
                )
            )
            setCallHeldTimeComparisonType(rptConfigurationDataResponseDto.FilterCallTimeHeldCmp)
            setCallHeldTimeValue1(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeHeldValue1,
                    language
                )
            )
            setCallHeldTimeValue2(
                convertDurationInSecondsToFormattedString(
                    rptConfigurationDataResponseDto.FilterCallTimeHeldValue2,
                    language
                )
            )

            // ACD Codes tab properties.
            setStatisticSettingsACDNACodes([
                {
                    codeIdentifier: 1,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode01 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 2,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode02 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 3,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode03 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 4,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode04 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 5,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode05 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 6,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode06 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 7,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode07 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 8,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode08 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 9,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode09 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 10,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode10 || NOT_DEFINED_VALUE_STRING
                },
                {
                    codeIdentifier: 11,
                    codeValue: rptConfigurationDataResponseDto.StatisticSettingACDNACode11 || NOT_DEFINED_VALUE_STRING
                }
            ])
            setUseCodeDescriptionForACDNACodeStatisticSettings(
                rptConfigurationDataResponseDto.StatisticSettingACDNACodeUseCodeDesc
            )

            // We need a new formatter for values in the request to populate state - the state properties might not be updated yet.
            // eslint-disable-next-line
            const newCurrencyFormatter: CDecimalFormatter = new com.Akixi.Text.CDecimalFormatter(
                rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyFormatPattern,
                String.fromCharCode(rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyGroupingSeparator),
                String.fromCharCode(rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyDecimalSeparator),
                mapCurrencyIdToCurrencySymbol(rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyID)
            )

            setCurrencyFormatter(newCurrencyFormatter)

            // // Financial tab properties.
            setMonetaryCallAssignmentsValues({
                inboundCallsAvgOrderVal: newCurrencyFormatter.Format(
                    rptConfigurationDataResponseDto.StatisticSettingFinancialOrderValueInbound
                ),
                inboundStrikeRate: rptConfigurationDataResponseDto.StatisticSettingFinancialStrikeRateInbound,
                outboundCallsAvgOrderVal: newCurrencyFormatter.Format(
                    rptConfigurationDataResponseDto.StatisticSettingFinancialOrderValueOutbound
                ),
                outboundStrikeRate: rptConfigurationDataResponseDto.StatisticSettingFinancialStrikeRateOutbound
            })

            setCurrencyDisplayFormatSettings({
                currencyId: rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyID,
                pattern: rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyFormatPattern,
                groupingSeparator: rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyGroupingSeparator,
                decimalSeparator: rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyDecimalSeparator
            })
        }
    }

    /**
     * Prepares statInfo request parameters from provided values and updates statistics configuration.
     *
     * @param reportDefinitions Available report definitions.
     * @param rptConfigurationDataResponseDto Report configuration.
     */
    const prepareStatInfoParametersAndUpdateStatisticsConfiguration = (
        reportDefinitions: ReportDefs,
        rptConfigurationDataResponseDto: RptConfigurationDataResponseDto
    ): void => {
        const reportType = rptConfigurationDataResponseDto.Type
        const selectedReportStyleByReportType = reportDefinitions?.find(
            (reportDef) => reportDef.ReportType === reportType
        )

        const retrieveStatInfoRequestParameters: RetrieveStatInfoRequestParameters = {
            reportType: String(selectedReportStyleByReportType?.ReportType),
            telSysID: String(rptConfigurationDataResponseDto.FilterTelephoneSystemID),
            partitionIDs: rptConfigurationDataResponseDto.FilterPartitionIDs,
            svcLvlAnsThres: String(rptConfigurationDataResponseDto.StatisticSettingSvcLvlAnsThres),
            callsInAnsThres1: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres1),
            callsInAnsThres2: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres2),
            callsInAnsThres3: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres3),
            callsInAnsThres4: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres4),
            callsInAnsThres5: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres5),
            callsInAnsThres6: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAnsThres6),
            svcLvlAbndThres: String(rptConfigurationDataResponseDto.StatisticSettingSvcLvlAbndThres),
            callsInAbndThres1: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres1),
            callsInAbndThres2: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres2),
            callsInAbndThres3: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres3),
            callsInAbndThres4: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres4),
            callsInAbndThres5: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres5),
            callsInAbndThres6: String(rptConfigurationDataResponseDto.StatisticSettingCallsInAbndThres6),
            ACDNACode1: rptConfigurationDataResponseDto.StatisticSettingACDNACode01 || '',
            ACDNACode2: rptConfigurationDataResponseDto.StatisticSettingACDNACode02 || '',
            ACDNACode3: rptConfigurationDataResponseDto.StatisticSettingACDNACode03 || '',
            ACDNACode4: rptConfigurationDataResponseDto.StatisticSettingACDNACode04 || '',
            ACDNACode5: rptConfigurationDataResponseDto.StatisticSettingACDNACode05 || '',
            ACDNACode6: rptConfigurationDataResponseDto.StatisticSettingACDNACode06 || '',
            ACDNACode7: rptConfigurationDataResponseDto.StatisticSettingACDNACode07 || '',
            ACDNACode8: rptConfigurationDataResponseDto.StatisticSettingACDNACode08 || '',
            ACDNACode9: rptConfigurationDataResponseDto.StatisticSettingACDNACode09 || '',
            ACDNACode10: rptConfigurationDataResponseDto.StatisticSettingACDNACode10 || '',
            ACDNACode11: rptConfigurationDataResponseDto.StatisticSettingACDNACode11 || '',
            ACDNACodeUseCodeDesc: String(rptConfigurationDataResponseDto.StatisticSettingACDNACodeUseCodeDesc)
        }

        // eslint-disable-next-line
        const currencyFormatterToUse: CDecimalFormatter = new com.Akixi.Text.CDecimalFormatter(
            rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyFormatPattern ||
                CURRENCYDEF_DEFAULT.patternDefault,
            rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyGroupingSeparator
                ? String.fromCharCode(
                      rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyGroupingSeparator
                  )
                : CURRENCYDEF_DEFAULT.groupingSeparatorDefault,
            rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyDecimalSeparator
                ? String.fromCharCode(rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyDecimalSeparator)
                : CURRENCYDEF_DEFAULT.decimalSeparatorDefault,
            rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyID
                ? mapCurrencyIdToCurrencySymbol(rptConfigurationDataResponseDto.StatisticSettingFinancialCurrencyID)
                : CURRENCYDEF_DEFAULT.currencySymbol
        )

        // Fields tab statistics properties
        reloadStatInfoAndSetStatisticsConfigurations(
            rptConfigurationDataResponseDto.StatisticsDisplay,
            rptConfigurationDataResponseDto.StatisticsSettings,
            retrieveStatInfoRequestParameters,
            currencyFormatterToUse
        )
    }

    /**
     * Reloads list of available statistics and populates list of configured statistics based on defaults in report style definition.
     * When this method runs, it replaces all currently configured statistics.
     */
    const resetStatisticsConfigurationToDefaultFromReportStyle = (reportStyle: ReportDefinition): Promise<void> => {
        let statisticsDisplay: string
        let statisticsSettings: string
        if (isListReport) {
            switch (displayedAsListReport) {
                case ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_BLFVIEW_HORIZONTAL:
                case ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_BLFVIEW_VERTICAL:
                    statisticsSettings = reportStyle.DefaultStatisticsIconViewSettings
                    statisticsDisplay = reportStyle.DefaultStatisticsIconViewDisplay
                    break
                case ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_CHART_VIEW:
                    statisticsSettings = reportStyle.DefaultStatisticsChartViewSettings
                    statisticsDisplay = reportStyle.DefaultStatisticsChartViewDisplay
                    break
                case ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_DETAILEDTABLEVIEW:
                default:
                    statisticsSettings = reportStyle.DefaultStatisticsTableViewSettings
                    statisticsDisplay = reportStyle.DefaultStatisticsTableViewDisplay
                    break
            }
        } else {
            switch (displayedAsWallboardReport) {
                case WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_CHART:
                    statisticsSettings = reportStyle.DefaultStatisticsChartViewSettings
                    statisticsDisplay = reportStyle.DefaultStatisticsChartViewDisplay
                    break
                case WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_GRID:
                case WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_COLUMNS:
                case WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_ROWS:
                default:
                    statisticsSettings = reportStyle.DefaultStatisticsTableViewSettings
                    statisticsDisplay = reportStyle.DefaultStatisticsTableViewDisplay
                    break
            }
        }

        return reloadStatInfoAndSetStatisticsConfigurations(
            statisticsDisplay,
            statisticsSettings,
            prepareStatInfoRequestParamsFromCurrentState(reportStyle),
            currencyFormatter
        )
    }

    /**
     * Initialises report configuration window with all data needed before first render.
     */
    const initialiseReportConfigurationData = async (): Promise<void> => {
        try {
            setReportDataLoading(true)

            // Retrieve report definitions.
            const reportPropertiesResponse = await retrieveReportProperties(servletPath)
            setReportDefs(reportPropertiesResponse)

            // Retrieve filter entity info.
            const filterEntityInfoResponse = await retrieveFilterEntityInfo(
                servletPath,
                telSys,
                partitions.join(),
                maxFilterListLimit
            )
            setFilterEntityInfo(filterEntityInfoResponse)

            if (!configurationTabs) {
                if (reportID) {
                    // Modal for update of existing report - load existing report configuration
                    const reportConfigurationDto: RptConfigurationDataResponseDto =
                        await loadExistingReportConfiguration(reportID)

                    mapReportConfigurationAndUpdateState(reportConfigurationDto, reportPropertiesResponse)

                    if (reportConfigurationDto.Type !== ReportTypes.DASHBOARD) {
                        // Stat Info is only needed for reports other than dashboard.
                        prepareStatInfoParametersAndUpdateStatisticsConfiguration(
                            reportPropertiesResponse,
                            reportConfigurationDto
                        )
                    } else {
                        // For dashboard report, we do not need statistics fields,
                        // but the same field in response represents configured reports and their layout.
                        setConfiguredDashboardReports(
                            mapStatisticsDisplayToListOfDashboardReports(reportConfigurationDto.StatisticsDisplay)
                        )
                        setSelectedDashboardLayoutStyle(
                            mapStatisticsSettingsToDashboardLayout(reportConfigurationDto.StatisticsSettings)
                        )
                    }
                } else {
                    // Add report dialog, set initial state.
                    setSelectedReportStyle(reportPropertiesResponse[0])
                    setReportName(reportPropertiesResponse[0].DescDefaultACDOrLower)
                    // Set default values from report definition.
                    setCallTypeDirection(reportPropertiesResponse[0].FilterCallTypeDefault)
                    setIncludeCallsForGrpDev(reportPropertiesResponse[0].FilterDevicesIfGrpIncGrpMmbrCalls)
                    resetStatisticsConfigurationToDefaultFromReportStyle(reportPropertiesResponse[0])
                }
            } else {
                mapFromPopoutData()
                $window.configurationTabs = undefined
            }
        } catch (error) {
            displayError(error as Error)
        } finally {
            setReportDataLoading(false)
        }
    }

    useEffect(() => {
        // Actually initialise component data. Runs once without dependencies.
        initialiseReportConfigurationData()
        setModalHelpTopicID(getHelpTopicByTab(activeTabID))
    }, [])

    useEffect(() => {
        if (selectedReportStyle) {
            // Do the re-initialisation only when we have selected report style.
            // This avoids to run this effect before the first default selected report style is set.
            //
            // - If the partition or telephone system were changed we have to reinitialise the filter entity info to get the
            // corresponding set of ACD agents, devices and other entities.
            initialiseRptFilterEntityInfo()
        }
    }, [telSys, partitions])

    const calculateSetDateTimeOptionSelected = useCallback((selectedRptStyle: ReportDefinition): number => {
        if (isPersonalDashboard.current) {
            return REPORT_DURATION_REALTIME
        }

        if (isCurrentTelSysHistoricRef.current) {
            return REPORT_DURATION_DAY_CURRENT
        }

        return selectedRptStyle?.DurationDefault
    }, [])

    useEffect(() => {
        if (selectedReportStyle) {
            // Do the re-initialisation only when we have selected report style.
            // This avoids to run this effect before the first default selected report style is set.

            // Updates the print tab related settings when new report style is selected.
            setPrintViewChartInclusion(selectedReportStyle.PrintViewChartInclusionDefault)

            // Updates the Report tab date time section to correct default date time option (duration).
            setDateTimeOptionSelected(calculateSetDateTimeOptionSelected(selectedReportStyle))
        }
    }, [selectedReportStyle])

    // Set statisticSettingsACDNACodes to default value when filterEntityInfo is loaded or changed.
    useEffect(() => {
        const newStatisticSettingsACDNACodes: ACDNACodeValue[] = []
        const entityInfoACDNAData = filterEntityInfo?.EntityInfo.CodesACDNotAvailable?.Data

        if (entityInfoACDNAData) {
            for (let i = 1; i <= 11; i++) {
                if (entityInfoACDNAData[i]) {
                    newStatisticSettingsACDNACodes.push({
                        codeIdentifier: i,
                        codeValue: entityInfoACDNAData[i].ID
                    })
                } else {
                    // If there is no code for this codeIdentifier (there were less than 10 codes provided in report entity info)
                    // fill in the remaining ACDNA Codes with not defined value.
                    newStatisticSettingsACDNACodes.push({
                        codeIdentifier: i,
                        codeValue: NOT_DEFINED_VALUE_STRING
                    })
                }
            }
        }

        // Only set statistic ACDNA codes if they are not already loaded / set up.
        if (statisticSettingsACDNACodes.length === 0) {
            setStatisticSettingsACDNACodes(newStatisticSettingsACDNACodes)
        }
    }, [filterEntityInfo])

    /**
     * Resets default values of answer targets.
     */
    const resetAnswerTargets = useCallback((): void => {
        setCallsInAnsThres1(15)
        setCallsInAnsThres2(30)
        setCallsInAnsThres3(60)
        setCallsInAnsThres4(90)
        setCallsInAnsThres5(120)
        setCallsInAnsThres6(300)
    }, [])

    /**
     * Resets default values of abandoned targets.
     */
    const resetAbandonedTargets = useCallback((): void => {
        setCallsInAbndThres1(60)
        setCallsInAbndThres2(90)
        setCallsInAbndThres3(120)
        setCallsInAbndThres4(150)
        setCallsInAbndThres5(180)
        setCallsInAbndThres6(300)
    }, [])

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
        setModalHelpTopicID(getHelpTopicByTab(newActiveTabID))
    }

    /**
     * Handler method for report style changes done by user interaction.
     * @param newReportStyle Newly selected report style.
     */
    const handleReportStyleChangeByUser = (newReportStyle: ReportDefinition): void => {
        setSelectedReportStyle(newReportStyle)
        // Set default values from report definition.
        setIncludeCallsForGrpDev(newReportStyle.FilterDevicesIfGrpIncGrpMmbrCalls)
        setCallTypeDirection(newReportStyle.FilterCallTypeDefault)
        setDeviceTypes([ALL_VALUE])

        // Reload is not needed if new report style is a dashboard, since dashboard does not allow Fields tab.
        if (newReportStyle.ReportType !== ReportTypes.DASHBOARD) {
            resetStatisticsConfigurationToDefaultFromReportStyle(newReportStyle)
        }
    }

    /**
     * Handler method for telephone system changes done by user interaction.
     * @param newTelSys New tel sys value.
     */
    const handleTelSysChangeByUser = (newTelSys: number): void => {
        if (telSys !== newTelSys) {
            setPartitions([ANY_VALUE_PARTITION_GUID])
        }
        setTelSys(newTelSys)
        setDateTimeOptionSelected(
            filterEntityInfo?.EntityInfo?.TelSvrs?.Data.find(
                (telephoneSys: TelephoneServerEntityDataItem): boolean => telephoneSys.ID === `${newTelSys}`
            )?.HistoricOnly ?? false
                ? REPORT_DURATION_DAY_CURRENT
                : REPORT_DURATION_REALTIME
        )
    }

    const resetStatisticsConfigurationToReportDefault = (): void => {
        if (selectedReportStyle) {
            resetStatisticsConfigurationToDefaultFromReportStyle(selectedReportStyle)
        }
    }

    // List of all tabs settings.
    const tabs: Tab[] = [
        {
            ID: ReportTabID.REPORT,
            isDisplayed: !!selectedReportStyle,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameReport),
                iconName: IconName.REPORT,
                onClick: (): void => activateTab(ReportTabID.REPORT)
            },
            content: (
                <ReportTab
                    isPersonalDashboard={isPersonalDashboard}
                    reportName={reportName}
                    setReportName={setReportName}
                    note={note}
                    setNote={setNote}
                    dateTimeOptionSelected={dateTimeOptionSelected}
                    setDateTimeOptionSelected={setDateTimeOptionSelected}
                    reportDefinitions={reportDefs}
                    selectedReportStyle={selectedReportStyle}
                    updateSelectedReportStyle={handleReportStyleChangeByUser}
                    displayedAsWallboardReport={displayedAsWallboardReport}
                    setDisplayedAsWallboardReport={setDisplayedAsWallBoardReport}
                    zoomLevel={zoomLevel}
                    setZoomLevel={setZoomLevel}
                    isZoomLevelCustom={isZoomLevelCustom}
                    setIsZoomLevelCustom={setIsZoomLevelCustom}
                    chartType={chartType}
                    setChartType={setChartType}
                    chartProvider={chartProvider}
                    setChartProvider={setChartProvider}
                    externalURL={externalURL}
                    setExternalURL={setExternalURL}
                    displayedAsListReport={displayedAsListReport}
                    setDisplayedAsListReport={setDisplayedAsListReport}
                    isShowingHeaderRowEnabled={isShowingHeaderRowEnabled}
                    setIsShowingHeaderRowEnabled={setIsShowingHeaderRowEnabled}
                    isShowingFooterRowEnabled={isShowingFooterRowEnabled}
                    setIsShowingFooterRowEnabled={setIsShowingFooterRowEnabled}
                    isShowingContentRowsEnabled={isShowingContentRowsEnabled}
                    setIsShowingContentRowsEnabled={setIsShowingContentRowsEnabled}
                    isShowingSpacerRowsEnabled={isShowingSpacerRowsEnabled}
                    setIsShowingSpacerRowsEnabled={setIsShowingSpacerRowsEnabled}
                    isShowingLargeBLFToolTipsEnabled={isShowingLargeBLFToolTipsEnabled}
                    setIsShowingLargeBLFToolTipsEnabled={setIsShowingLargeBLFToolTipsEnabled}
                    isAutomaticTextTruncationEnabled={isAutomaticTextTruncationEnabled}
                    setIsAutomaticTextTruncationEnabled={setIsAutomaticTextTruncationEnabled}
                    isWallboardReport={isWallboardReport}
                    isDashboardReport={isDashboardReport}
                    isExternalReport={isExternalReport}
                    isTotalCallsByMinutesInterval={isTotalCallsByMinutesInterval}
                    isListReport={isListReport}
                    folderPath={folderPath}
                    setFolderPath={setFolderPath}
                    filterEntityInfo={filterEntityInfo}
                    reportTimeRangeStart={reportTimeRangeStart}
                    setReportTimeRangeStart={setReportTimeRangeStart}
                    reportTimeRangeEnd={reportTimeRangeEnd}
                    setReportTimeRangeEnd={setReportTimeRangeEnd}
                    reportDateTimeRangeStart={reportDateTimeRangeStart}
                    setReportDateTimeRangeStart={setReportDateTimeRangeStart}
                    reportDateTimeRangeEnd={reportDateTimeRangeEnd}
                    setReportDateTimeRangeEnd={setReportDateTimeRangeEnd}
                    reportDateTimeDaysOfTheWeek={reportDateTimeDaysOfTheWeek}
                    setReportDateTimeDaysOfTheWeek={setReportDateTimeDaysOfTheWeek}
                    reportDateTimeLastXDays={reportDateTimeLastXDays}
                    setReportDateTimeLastXDays={setReportDateTimeLastXDays}
                    totalCallByInterval={totalCallByInterval}
                    setTotalCallByInterval={setTotalCallByInterval}
                    isCallsByInterval={isCallsByInterval}
                    callByIntervalType={callByIntervalType}
                    setCallByIntervalType={setCallByIntervalType}
                    isCurrentTelSysHistoric={isCurrentTelSysHistoric}
                />
            )
        },
        {
            ID: ReportTabID.FILTER,
            isDisplayed: !!selectedReportStyle && !isDashboardReport && !isExternalReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameFilter),
                iconName: IconName.FILTER,
                onClick: (): void => activateTab(ReportTabID.FILTER),
                className: 'filter-tab-menu-item'
            },
            content: (
                <FilterTab
                    telSys={telSys}
                    handleTelSysChangeByUser={handleTelSysChangeByUser}
                    partitions={partitions}
                    setPartitions={setPartitions}
                    telephoneNos={telephoneNos}
                    setTelephoneNos={setTelephoneNos}
                    telephoneNoDescriptions={telephoneNoDescriptions}
                    setTelephoneNoDescriptions={setTelephoneNoDescriptions}
                    devices={devices}
                    setDevices={setDevices}
                    ACDAgents={ACDAgents}
                    setACDAgents={setACDAgents}
                    filterLocationToFrom={filterLocationToFrom}
                    setFilterLocationToFrom={setFilterLocationToFrom}
                    channelTypes={channelTypes}
                    setChannelTypes={setChannelTypes}
                    callTypeDirection={callTypeDirection}
                    setCallTypeDirection={setCallTypeDirection}
                    answerType={answerType}
                    setAnswerType={setAnswerType}
                    callerID={callerID}
                    setCallerID={setCallerID}
                    DDIDigits={DDIDigits}
                    setDDIDigits={setDDIDigits}
                    accountCodes={accountCodes}
                    setAccountCodes={setAccountCodes}
                    callIdentifier={callIdentifier}
                    setCallIdentifier={setCallIdentifier}
                    subjectFilter={subjectFilter}
                    setSubjectFilter={setSubjectFilter}
                    treatConfCallAsExternal={treatConfCallAsExternal}
                    setTreatConfCallAsExternal={setTreatConfCallAsExternal}
                    filterEntityInfo={filterEntityInfo}
                    supportsSubjectFiltering={!!selectedReportStyle && selectedReportStyle.SupportsSubjectFiltering}
                    isReportTemplateUser={isReportTemplateUser}
                    reportType={selectedReportStyle?.ReportType}
                    currentTelSysType={currentlySelectedTelephoneServerType}
                    deviceTypes={deviceTypes}
                    setDeviceTypes={setDeviceTypes}
                />
            )
        },
        {
            ID: ReportTabID.OTHER,
            isDisplayed:
                isAdvancedMode && !!selectedReportStyle && !isDashboardReport && !isExternalReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: (
                    <>
                        {language?.get(ConfigureReportLanguage.TabnameOther)}
                        <HelpTag
                            className='tab-help-tag'
                            helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_OTHER_TAB}
                            tag={Tags.CONFIGURE_REPORT}
                            position='right'
                        />
                    </>
                ),
                iconName: IconName.OTHER,
                onClick: (): void => activateTab(ReportTabID.OTHER)
            },
            content: (
                <OtherTab
                    filterEntityInfo={filterEntityInfo}
                    telnosMovedTo={telnosMovedTo}
                    setTelnosMovedTo={setTelnosMovedTo}
                    devicesMovedFrom={devicesMovedFrom}
                    setDevicesMovedFrom={setDevicesMovedFrom}
                    ACDAgentsMovedFrom={ACDAgentsMovedFrom}
                    setACDAgentsMovedFrom={setACDAgentsMovedFrom}
                    callSegmentNumber={callSegmentNumber}
                    setCallSegmentNumber={setCallSegmentNumber}
                    callSegmentStartReason={callSegmentStartReason}
                    setCallSegmentStartReason={setCallSegmentStartReason}
                    callSegmentEndReason={callSegmentEndReason}
                    setCallSegmentEndReason={setCallSegmentEndReason}
                    callSegmentChainID={callSegmentChainID}
                    setCallSegmentChainID={setCallSegmentChainID}
                    ringTimeDistComparisonType={ringTimeDistComparisonType}
                    setRingTimeDistComparisonType={setRingTimeDistComparisonType}
                    ringTimeDistValue1={ringTimeDistValue1}
                    setRingTimeDistValue1={setRingTimeDistValue1}
                    ringTimeDistValue2={ringTimeDistValue2}
                    setRingTimeDistValue2={setRingTimeDistValue2}
                    ringTimeSegmentComparisonType={ringTimeSegmentComparisonType}
                    setRingTimeSegmentComparisonType={setRingTimeSegmentComparisonType}
                    ringTimeSegmentValue1={ringTimeSegmentValue1}
                    setRingTimeSegmentValue1={setRingTimeSegmentValue1}
                    ringTimeSegmentValue2={ringTimeSegmentValue2}
                    setRingTimeSegmentValue2={setRingTimeSegmentValue2}
                    callTalkTimeComparisonType={callTalkTimeComparisonType}
                    setCallTalkTimeComparisonType={setCallTalkTimeComparisonType}
                    callTalkTimeValue1={callTalkTimeValue1}
                    setCallTalkTimeValue1={setCallTalkTimeValue1}
                    callTalkTimeValue2={callTalkTimeValue2}
                    setCallTalkTimeValue2={setCallTalkTimeValue2}
                    totalCallTimeComparisonType={totalCallTimeComparisonType}
                    setTotalCallTimeComparisonType={setTotalCallTimeComparisonType}
                    totalCallTimeValue1={totalCallTimeValue1}
                    setTotalCallTimeValue1={setTotalCallTimeValue1}
                    totalCallTimeValue2={totalCallTimeValue2}
                    setTotalCallTimeValue2={setTotalCallTimeValue2}
                    callHeldTimeComparisonType={callHeldTimeComparisonType}
                    setCallHeldTimeComparisonType={setCallHeldTimeComparisonType}
                    callHeldTimeValue1={callHeldTimeValue1}
                    setCallHeldTimeValue1={setCallHeldTimeValue1}
                    callHeldTimeValue2={callHeldTimeValue2}
                    setCallHeldTimeValue2={setCallHeldTimeValue2}
                    filterDurationRingSupported={
                        !!selectedReportStyle && selectedReportStyle.FilterDurationsRingSupported
                    }
                    filterDurationsOtherSupported={
                        !!selectedReportStyle && selectedReportStyle.FilterDurationsOtherSupported
                    }
                    isReportTemplateUser={isReportTemplateUser}
                    currentTelSysType={currentlySelectedTelephoneServerType}
                />
            )
        },
        {
            ID: ReportTabID.FIELDS,
            isDisplayed: !!selectedReportStyle && !isDashboardReport && !isExternalReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameFields),
                iconName: IconName.FIELDS,
                onClick: (): void => activateTab(ReportTabID.FIELDS),
                className: 'fields-tab-menu-item'
            },
            content: (
                <FieldsTab
                    isWallboard={isWallboardReport}
                    statsList={currentlyAvailableStatisticsFields}
                    resetStatisticsConfigurationToReportDefault={resetStatisticsConfigurationToReportDefault}
                    configuredStatisticsConfigurations={configuredStatisticsConfigurations}
                    setConfiguredStatisticsConfigurations={setConfiguredStatisticsConfigurations}
                    isReportRealTime={dateTimeOptionSelected === REPORT_DURATION_REALTIME}
                    currentReportStyleAlarmTypes={currentReportStyleAlarmTypes}
                    isReportDisplayedAsChart={
                        displayedAsListReport ===
                            ListReportDisplayedAsValues.DISPLAYED_AS_LISTREPORT_OPTION_VALUE_CHART_VIEW ||
                        displayedAsWallboardReport === WallboardDisplayedAsValues.DISPLAYED_AS_OPTION_VALUE_CHART
                    }
                    currentReportStyleSupportsColouredStats={selectedReportStyle?.SupportsColouredStats || false}
                    currentReportStyleSupportsStatDisplayedAs={selectedReportStyle?.SupportsStatDisplayedAs || false}
                    currentReportStyleSupportsSortingEver={selectedReportStyle?.SupportsSortingEver || false}
                    currentReportStyleSupportsSortingRealTime={selectedReportStyle?.SupportsRealTimeReSorting || false}
                    currencyFormatter={currencyFormatter}
                    rptFilterEntityInfo={filterEntityInfo}
                    isUserLicensedForCurrentReportStyle={isUserLicensedForCurrentReportStyle}
                />
            )
        },
        {
            ID: ReportTabID.DASHBOARD,
            isDisplayed: !!selectedReportStyle && isDashboardReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameDashboard),
                iconName: IconName.DASHBOARD,
                onClick: (): void => activateTab(ReportTabID.DASHBOARD)
            },
            content: (
                <DashboardTab
                    setSelectedDashboardLayoutStyle={setSelectedDashboardLayoutStyle}
                    selectedDashboardLayoutStyle={selectedDashboardLayoutStyle}
                />
            )
        },
        {
            ID: ReportTabID.SLAKPI,
            isDisplayed:
                isAdvancedMode &&
                !!selectedReportStyle &&
                (selectedReportStyle.SupportsCalculatedStats ||
                    selectedReportStyle.SupportsInboundStatSettings ||
                    selectedReportStyle.SupportsExclusionOfHeldTimeFromTalkTimeSettings) &&
                !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameSlakpi),
                iconName: IconName.LAYER,
                onClick: (): void => activateTab(ReportTabID.SLAKPI)
            },
            content: (
                <SLAKPITab
                    callsAnsWithin={callsAnsWithin}
                    setCallsAnsWithin={setCallsAnsWithin}
                    isSrvLvlIgnQuickAbndCallsWithinEnabled={isSrvLvlIgnQuickAbndCallsWithinEnabled}
                    setSrvLvlIgnQuickAbndCallsWithinEnabled={setIsSrvLvlIgnQuickAbndCallsWithinEnabled}
                    srvLvlIgnQuickAbndCallsWithin={srvLvlIgnQuickAbndCallsWithin}
                    setSrvLvlIgnQuickAbndCallsWithin={setSrvLvlIgnQuickAbndCallsWithin}
                    isSrvLvlIgnOvrflwdCallsWithinEnabled={isSrvLvlIgnOvrflwdCallsWithinEnabled}
                    setSrvLvlIgnOvrflwdCallsWithinEnabled={setIsSrvLvlIgnOvrflwdCallsWithinEnabled}
                    srvLvlIgnOvrflwdCallsWithin={srvLvlIgnOvrflwdCallsWithin}
                    setSrvLvlIgnOvrflwdCallsWithin={setSrvLvlIgnOvrflwdCallsWithin}
                    isInbStIgnQuickAbndCallsWithinEnabled={isInbStIgnQuickAbndCallsWithinEnabled}
                    setInbStIgnQuickAbndCallsWithinEnabled={setIsInbStIgnQuickAbndCallsWithinEnabled}
                    inbStIgnQuickAbndCallsWithin={inbStIgnQuickAbndCallsWithin}
                    setInbStIgnQuickAbndCallsWithin={setInbStIgnQuickAbndCallsWithin}
                    isInbStIgnOvrflwdCallsWithinEnabled={isInbStIgnOvrflwdCallsWithinEnabled}
                    setInbStIgnOvrflwdCallsWithinEnabled={setIsInbStIgnOvrflwdCallsWithinEnabled}
                    inbStIgnOvrflwdCallsWithin={inbStIgnOvrflwdCallsWithin}
                    setInbStIgnOvrflwdCallsWithin={setInbStIgnOvrflwdCallsWithin}
                    callsInAnsThres1={callsInAnsThres1}
                    setCallsInAnsThres1={setCallsInAnsThres1}
                    callsInAnsThres2={callsInAnsThres2}
                    setCallsInAnsThres2={setCallsInAnsThres2}
                    callsInAnsThres3={callsInAnsThres3}
                    setCallsInAnsThres3={setCallsInAnsThres3}
                    callsInAnsThres4={callsInAnsThres4}
                    setCallsInAnsThres4={setCallsInAnsThres4}
                    callsInAnsThres5={callsInAnsThres5}
                    setCallsInAnsThres5={setCallsInAnsThres5}
                    callsInAnsThres6={callsInAnsThres6}
                    setCallsInAnsThres6={setCallsInAnsThres6}
                    resetAnswerTargets={resetAnswerTargets}
                    callsInAbndThres1={callsInAbndThres1}
                    setCallsInAbndThres1={setCallsInAbndThres1}
                    callsInAbndThres2={callsInAbndThres2}
                    setCallsInAbndThres2={setCallsInAbndThres2}
                    callsInAbndThres3={callsInAbndThres3}
                    setCallsInAbndThres3={setCallsInAbndThres3}
                    callsInAbndThres4={callsInAbndThres4}
                    setCallsInAbndThres4={setCallsInAbndThres4}
                    callsInAbndThres5={callsInAbndThres5}
                    setCallsInAbndThres5={setCallsInAbndThres5}
                    callsInAbndThres6={callsInAbndThres6}
                    setCallsInAbndThres6={setCallsInAbndThres6}
                    resetAbandonedTargets={resetAbandonedTargets}
                    supportsCalculatedStatistics={!!selectedReportStyle && selectedReportStyle.SupportsCalculatedStats}
                    supportsInboundStatSettings={
                        !!selectedReportStyle && selectedReportStyle.SupportsInboundStatSettings
                    }
                    supportsExclusionOfHeldTimeFromTalkTimeSettings={
                        !!selectedReportStyle && selectedReportStyle.SupportsExclusionOfHeldTimeFromTalkTimeSettings
                    }
                    includeEndedOnHold={includeEndedOnHold}
                    setIncludeEndedOnHold={setIncludeEndedOnHold}
                    includeEscaped={includeEscaped}
                    setIncludeEscaped={setIncludeEscaped}
                    excludeHeldTime={excludeHeldTime}
                    setExcludeHeldTime={setExcludeHeldTime}
                />
            )
        },
        {
            ID: ReportTabID.ALARMS,
            isDisplayed:
                isAdvancedMode &&
                !!selectedReportStyle &&
                selectedReportStyle.AlarmTypes > iREPORT_ALARM_CAPABILITY_NONE &&
                dateTimeOptionSelected === REPORT_DURATION_REALTIME &&
                !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameAlarms),
                iconName: IconName.ALARM,
                onClick: (): void => activateTab(ReportTabID.ALARMS)
            },
            content: (
                <AlarmsTab
                    alarmTypesFlags={
                        selectedReportStyle ? selectedReportStyle.AlarmTypes : iREPORT_ALARM_CAPABILITY_NONE
                    }
                    isOnAvailableStateEnabled={isOnAvailableStateEnabled}
                    setIsOnAvailableStateEnabled={setIsOnAvailableStateEnabled}
                    onAvailableState={onAvailableState}
                    setOnAvailableState={setOnAvailableState}
                    isOnBusyStateEnabled={isOnBusyStateEnabled}
                    setIsOnBusyStateEnabled={setIsOnBusyStateEnabled}
                    onBusyState={onBusyState}
                    setOnBusyState={setOnBusyState}
                    isOnNAOrDNDStateEnabled={isOnNAOrDNDStateEnabled}
                    setIsOnNAOrDNDStateEnabled={setIsOnNAOrDNDStateEnabled}
                    onNAOrDNDState={onNAOrDNDState}
                    setOnNAOrDNDState={setOnNAOrDNDState}
                    isOnWrapupStateEnabled={isOnWrapupStateEnabled}
                    setIsOnWrapupStateEnabled={setIsOnWrapupStateEnabled}
                    onWrapupState={onWrapupState}
                    setOnWrapupState={setOnWrapupState}
                    isOnInbAlertingEnabled={isOnInbAlertingEnabled}
                    setIsOnInbAlertingEnabled={setIsOnInbAlertingEnabled}
                    onInbAlerting={onInbAlerting}
                    setOnInbAlerting={setOnInbAlerting}
                    isOnTalkTimeEnabled={isOnTalkTimeEnabled}
                    setIsOnTalkTimeEnabled={setIsOnTalkTimeEnabled}
                    onTalkTime={onTalkTime}
                    setOnTalkTime={setOnTalkTime}
                    alarmOnNewAbandoned={alarmOnNewAbandoned}
                    setAlarmOnNewAbandoned={setAlarmOnNewAbandoned}
                    playSound={playSound}
                    setPlaySound={setPlaySound}
                    activeWindow={activeWindow}
                    setActiveWindow={setActiveWindow}
                />
            )
        },
        {
            ID: ReportTabID.SCHEDULING,
            isDisplayed:
                !!selectedReportStyle &&
                selectedReportStyle.SupportsScheduling &&
                !isDateTimeOnly &&
                !isPersonalDashboard.current &&
                !isReportTemplateUser,
            tabsMenuItem: {
                title: (
                    <>
                        {language?.get(ConfigureReportLanguage.TabnameScheduling)}
                        <HelpTag
                            className='tab-help-tag'
                            helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_SCHEDULING_TAB}
                            tag={Tags.CONFIGURE_REPORT}
                            position='right'
                        />
                    </>
                ),
                iconName: IconName.SCHEDULE,
                onClick: (): void => activateTab(ReportTabID.SCHEDULING),
                className: 'schedule-tab-menu-item'
            },
            content: (
                <SchedulingTab
                    scheduleReportOn={scheduleReportOn}
                    setScheduleReportOn={setScheduleReportOn}
                    runOnDate={runOnDate}
                    setRunOnDate={setRunOnDate}
                    runAtTime={runAtTime}
                    setRunAtTime={setRunAtTime}
                    runOnDays={runOnDays}
                    setRunOnDays={setRunOnDays}
                    runAtMinute={runAtMinute}
                    setRunAtMinute={setRunAtMinute}
                    runOnDayInWeek={runOnDayInWeek}
                    setRunOnDayInWeek={setRunOnDayInWeek}
                    runOnDayInMonth={runOnDayInMonth}
                    setRunOnDayInMonth={setRunOnDayInMonth}
                    runBetweenStart={runBetweenStart}
                    setRunBetweenStart={setRunBetweenStart}
                    runBetweenEnd={runBetweenEnd}
                    setRunBetweenEnd={setRunBetweenEnd}
                    targetRptFormat={targetRptFormat}
                    setTargetRptFormat={setTargetRptFormat}
                    useLanguage={useLanguage}
                    setUseLanguage={setUseLanguage}
                    paperSize={paperSize}
                    setPaperSize={setPaperSize}
                    autoCalcDuration={autoCalcDuration}
                    setAutoCalcDuration={setAutoCalcDuration}
                    bundleEmailAttachAsZip={bundleEmailAttachAsZip}
                    setBundleEmailAttachAsZip={setBundleEmailAttachAsZip}
                    runWithSimilarSchedules={runWithSimilarSchedules}
                    setRunWithSimilarSchedules={setRunWithSimilarSchedules}
                    setResetSchedule={setResetSchedule}
                    resetSchedule={resetSchedule}
                    deleteSchedule={deleteSchedule}
                    setDeleteSchedule={setDeleteSchedule}
                    lastRunningAt={scheduleLastRunningAt}
                    setLastRunningAt={setScheduleLastRunningAt}
                    nextRunningAt={scheduleNextRunningAt}
                    setNextRunningAt={setScheduleNextRunningAt}
                    statusError={scheduleStatusError}
                    setStatusError={setScheduleStatusError}
                    isScheduleActive={isScheduleActive}
                />
            )
        },
        {
            ID: ReportTabID.ACDCODES,
            isDisplayed:
                isAdvancedMode &&
                !!selectedReportStyle &&
                selectedReportStyle.SupportsACDStatsForNACodes &&
                shouldDisplayACDCodesTab &&
                !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameAcdCodes),
                iconName: IconName.TAG,
                onClick: (): void => activateTab(ReportTabID.ACDCODES)
            },
            content: (
                <ACDCodesTab
                    statisticSettingsACDNACodes={statisticSettingsACDNACodes}
                    useCodeDescriptionForACDNACodeStatisticSettings={useCodeDescriptionForACDNACodeStatisticSettings}
                    setUseCodeDescriptionForACDNACodeStatisticSettings={
                        setUseCodeDescriptionForACDNACodeStatisticSettings
                    }
                    setStatisticSettingsACDNACodes={setStatisticSettingsACDNACodes}
                    rptFilterEntityInfo={filterEntityInfo}
                    reportLoaded={reportLoaded.current}
                />
            )
        },
        {
            ID: ReportTabID.FINANCIAL,
            isDisplayed:
                isAdvancedMode &&
                !!selectedReportStyle &&
                (selectedReportStyle.SupportsFinancialStatsInbound ||
                    selectedReportStyle.SupportsFinancialStatsOutbound) &&
                !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameFinancial),
                iconName: IconName.MONEY,
                onClick: (): void => activateTab(ReportTabID.FINANCIAL)
            },
            content: (
                <FinancialTab
                    currencyDisplayFormatSettings={currencyDisplayFormatSettings}
                    handleCurrencyDisplayFormatSettingsChange={handleCurrencyDisplayFormatSettingsChange}
                    monetaryCallAssignmentsValues={monetaryCallAssignmentsValues}
                    setMonetaryCallAssignmentsValues={setMonetaryCallAssignmentsValues}
                    currencyFormatter={currencyFormatter}
                    supportsFinancialStatsInbound={!!selectedReportStyle?.SupportsFinancialStatsInbound}
                    supportsFinancialStatsOutbound={!!selectedReportStyle?.SupportsFinancialStatsOutbound}
                />
            )
        },
        {
            ID: ReportTabID.SETTINGS,
            isDisplayed:
                isAdvancedMode &&
                !!selectedReportStyle &&
                !isDateTimeOnly &&
                !isPersonalDashboard.current &&
                !isReportTemplateUser,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameSettings),
                iconName: IconName.SETTINGS,
                onClick: (): void => activateTab(ReportTabID.SETTINGS)
            },
            content: (
                <SettingsTab
                    includeInTransitioningSequence={includeInTransitioningSequence}
                    setIncludeInTransitioningSequence={setIncludeInTransitioningSequence}
                    rptDisplIntervalTime={rptDisplIntervalTime}
                    setRptDisplIntervalTime={setRptDisplIntervalTime}
                />
            )
        },
        {
            ID: ReportTabID.PRINT,
            isDisplayed:
                isAdvancedMode && !!selectedReportStyle && !isDashboardReport && !isExternalReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnamePrint),
                iconName: IconName.PRINT,
                onClick: (): void => activateTab(ReportTabID.PRINT)
            },
            content: (
                <PrintTab
                    isPrintShowingHeaderRowEnabled={isPrintShowingHeaderRowEnabled}
                    setIsPrintShowingHeaderRowEnabled={setIsPrintShowingHeaderRowEnabled}
                    isPrintShowingFooterRowEnabled={isPrintShowingFooterRowEnabled}
                    setIsPrintShowingFooterRowEnabled={setIsPrintShowingFooterRowEnabled}
                    isPrintShowingContentRowsEnabled={isPrintShowingContentRowsEnabled}
                    setIsPrintShowingContentRowsEnabled={setIsPrintShowingContentRowsEnabled}
                    isPrintShowingSpacerRowsEnabled={isPrintShowingSpacerRowsEnabled}
                    setIsPrintShowingSpacerRowsEnabled={setIsPrintShowingSpacerRowsEnabled}
                    isShowSummaryHeaderEnabled={isPrintShowSummaryHeaderEnabled}
                    setIsShowSummaryHeaderEnabled={setIsPrintShowSummaryHeaderEnabled}
                    printViewChartInclusion={printViewChartInclusion}
                    setPrintViewChartInclusion={setPrintViewChartInclusion}
                    isReportChartingFeatureEnabled={isReportChartingFeatureEnabled}
                />
            )
        },
        {
            ID: ReportTabID.ADVANCED,
            isDisplayed: isAdvancedMode && !!selectedReportStyle && !isDashboardReport && !isDateTimeOnly,
            tabsMenuItem: {
                title: language?.get(ConfigureReportLanguage.TabnameAdvanced),
                iconName: IconName.WRENCH,
                onClick: (): void => activateTab(ReportTabID.ADVANCED)
            },
            content: (
                <AdvancedTab
                    isWallboardReport={isWallboardReport}
                    isExternalReport={isExternalReport}
                    includeCallsForGrpDevEnabled={!!selectedReportStyle?.FilterDevicesIfGrpIncGrpMmbrCallsSupported}
                    includeCallsForGrpDev={includeCallsForGrpDev}
                    setIncludeCallsForGrpDev={setIncludeCallsForGrpDev}
                    calcACDStatsForAgntsSignedIntoSpecGrpEnabled={!!selectedReportStyle?.SupportsACDStats}
                    calcACDStatsForAgntsSignedIntoSpecGrp={calcACDStatsForAgntsSignedIntoSpecGrp}
                    setCalcACDStatsForAgntsSignedIntoSpecGrp={setCalcACDStatsForAgntsSignedIntoSpecGrp}
                    considerCallsMvdToUnresAsOverflwd={considerCallsMvdToUnresAsOverflwd}
                    setConsiderCallsMvdToUnresAsOverflwd={setConsiderCallsMvdToUnresAsOverflwd}
                    maxFilterListLimit={maxFilterListLimit}
                    setMaxFilterListLimit={setMaxFilterListLimit}
                    maxRptRowDisplLimit={maxRptRowDisplLimit}
                    setMaxRptRowDisplLimit={setMaxRptRowDisplLimit}
                    formatCSVStatValuesType={formatCSVStatValuesType}
                    setFormatCSVStatValuesType={setFormatCSVStatValuesType}
                    enableRptRequestAudit={enableRptRequestAudit}
                    setEnableRptRequestAudit={setEnableRptRequestAudit}
                    initialiseRptFilterEntityInfo={initialiseRptFilterEntityInfo}
                    isReportTemplateUser={isReportTemplateUser}
                    currentTelSysType={currentlySelectedTelephoneServerType ?? TELEPHONY_SYSTEM_TYPE_UNKNOWN}
                />
            )
        }
    ]

    const submitDisabled: boolean =
        !selectedReportStyle ||
        isReportDataLoading ||
        isReportListLoading ||
        isFilterEntityInfoLoading ||
        isStatsInfoLoading ||
        isStatsInfoUpdateLoading ||
        (!hasStatConfigurationsLoaded && selectedReportStyle.ReportType !== ReportTypes.DASHBOARD)

    return (
        <MuiPickersUtilsProvider utils={MomentUtils} locale={userInfo?.SessionInfo.Language}>
            <div className='configuration-tabs'>
                <div className='ct-basic-advanced'>
                    <Switch
                        checked={isAdvancedMode}
                        handleChange={setAdvancedMode}
                        className='advanced-mode-switch'
                        labelOff={language.get(ConfigureReportLanguage.ConfigureReport_Basic)}
                        labelOn={language.get(ConfigureReportLanguage.ConfigureReport_Advanced)}
                    />
                    <HelpTag
                        helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_BASIC_ADVANCED}
                        tag={Tags.CONFIGURE_REPORT}
                    />
                </div>
                {isReportDataLoading || isSubmitting ? (
                    <TabsContentLoader />
                ) : (
                    <React.Fragment>
                        <Tabs tabs={tabs} activeTabID={activeTabID} />
                        <FooterActions
                            activeTabID={activeTabID}
                            isSubmitDisabled={submitDisabled}
                            servletPath={servletPath}
                            closeModal={handleCancelModal}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            submitReportConfiguration={submitReportConfiguration}
                        />
                    </React.Fragment>
                )}
            </div>
        </MuiPickersUtilsProvider>
    )
}

export default ConfigurationTabs
